import { useEffect, useState, Fragment } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  PaperClipIcon,
  CheckIcon,
} from "@heroicons/react/outline";

import { Transition, Dialog } from "@headlessui/react";

import { toast } from "react-toastify";
import * as api from "./apiService";
import ReactTimeAgo from "react-time-ago";

import { Switch } from "@headlessui/react";

import Loader from "../../components/loader/Loader";
import JobCompleteModal from "./JobCompleteModal";
import JobPriceBreakdownModal from "./JobPriceBreakdownModal";
import JobCancelModal from "./JobCancelModal";
import JobReturnModal from "./JobReturnModal";

import JobCategoriesModal from "./JobCategoriesModal";
import AddServiceModal from "./AddServiceModal";
import AddRetainerServiceModal from "./AddRetainerServiceModal";
import JobFileUploadModal from "./JobFileUploadModal";
import JobInvoiceModal from "./JobInvoiceModal";
import JobNotInvoiceModal from "./JobNotInvoiceModal";
import JobInitialInspectionModal from "./JobInitialInspectionModal";
import JobInspectionChecklistModal from "./JobInspectionChecklistModal";

import JobComments from "./JobComments";

import CustomerFeedback from "./CustomerFeedback";
import JobDetailsOverview from "./JobDetailsOverview";

import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../../routes/userProfile/userSlice";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const XMarkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
};

const JobInfo = ({ reviewActions, reviewMenu } = {}) => {
  const { jobId } = useParams();
  const [loading, setLoading] = useState(false);
  const [jobDetails, setJobDetails] = useState({
    service_assignments: [],
    retainer_service_assignments: [],
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [isCompleteJobModalOpen, setCompleteJobModalOpen] = useState(false);
  const [isCancelJobModalOpen, setIsCancelJobModalOpen] = useState(false);
  const [isReturnJobModalOpen, setReturnJobModalOpen] = useState(false);
  const [isJobFileUploadModalOpen, setJobFileUploadModalOpen] = useState(false);
  const [isJobInvoiceModalOpen, setJobInvoiceModalOpen] = useState(false);
  const [isJobNotInvoiceModalOpen, setJobNotInvoiceModalOpen] = useState(false);

  const [savingAssignedPmPriceVisibility, setSavingAssignedPmPriceVisibility] =
    useState(false);

  const [isVendorAccepted, setIsVendorAccepted] = useState(false);

  const [serviceActivities, setServiceActivities] = useState([]);

  const [isPriceBreakdownModalOpen, setPriceBreakdownModalOpen] =
    useState(false);

  const [isCategoriesModalOpen, setCategoriesModalOpen] = useState(false);

  const [isAddServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [isAddRetainerServiceModalOpen, setAddRetainerServiceModalOpen] =
    useState(false);

  const [isInitialInspectionModalOpen, setInitialInspectionModalOpen] =
    useState(false);

  const [isInspectionChecklistModalOpen, setInspectionChecklistModalOpen] =
    useState(false);

  const [showActions, setShowActions] = useState(false);
  const currentUser = useAppSelector(selectUser);
  const navigate = useNavigate();
  const location = useLocation();
  const isCompletedReview = /^\/completed\/review\/[^/]+(?:\/photos(?:\/listing)?)?\/?$/.test(location.pathname);
  const isJobDetailsRoute =
    location.pathname.endsWith("/details") ||
    /^\/jobs\/[^/]+\/?$/.test(location.pathname);

  const [isAdmin, setIsAdmin] = useState(false);

  const [tailDetailsFound, setTailDetailsFound] = useState(false);

  const [tailDetailsId, setTailDetailsId] = useState(null);
  const [tailDetails, setTailDetails] = useState(null);

  const [priceBreakdown, setPriceBreakdown] = useState({});
  const [priceBreakdownComparison, setPriceBreakdownComparison] = useState({});

  const [steps, setSteps] = useState([]);

  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [jobFormOptions, setJobFormOptions] = useState({
    customers: [],
    aircraft_types: [],
    airports: [],
    fbos: [],
    tags: [],
    vendors: [],
  });
  const [callSign, setCallSign] = useState("");
  const [savingJobSection, setSavingJobSection] = useState(null);

  const [flights, setFlights] = useState([]);

  const [inspectionType, setInspectionType] = useState("I");

  const [openFboDetails, setOpenFboDetails] = useState(false);
  const [openAirportDetails, setOpenAirportDetails] = useState(false);

  const [fboForm, setFboForm] = useState({
    hours_of_operation: "",
    phone_number: "",
    website: "",
    special_notes: "",
  });
  const [savingFbo, setSavingFbo] = useState(false);

  const [airportForm, setAirportForm] = useState({
    is_wet_wash_allowed: false,
    special_notes: "",
  });

  const [savingAirport, setSavingAirport] = useState(false);

  const [openVendorDetails, setOpenVendorDetails] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);

  const [vendorForm, setVendorForm] = useState({
    id: null,
    name: "",
    phone_numbers: "",
    emails: "",
    billing_address: "",
    notes: "",
  });

  const canEditFbo =
    currentUser?.isAdmin ||
    currentUser?.isSuperUser ||
    currentUser?.isAccountManager;

  const canEditAirport =
    currentUser?.isAdmin ||
    currentUser?.isSuperUser ||
    currentUser?.isAccountManager;

  const canEditVendor =
    currentUser?.isAdmin ||
    currentUser?.isSuperUser ||
    currentUser?.isAccountManager;

  useEffect(() => {
    getJobDetails();
  }, []);

  // sync form values when modal opens or jobDetails change
  useEffect(() => {
    if (openFboDetails && jobDetails?.fbo) {
      setFboForm({
        hours_of_operation: jobDetails.fbo.hours_of_operation ?? "",
        phone_number: jobDetails.fbo.phone_number ?? "",
        website: jobDetails.fbo.website ?? "",
        special_notes: jobDetails.fbo.special_notes ?? "",
      });
    }
  }, [openFboDetails, jobDetails]);

  useEffect(() => {
    if (openAirportDetails && jobDetails?.airport) {
      setAirportForm({
        is_wet_wash_allowed: jobDetails.airport.is_wet_wash_allowed,
        special_notes: jobDetails.airport.special_notes ?? "",
      });
    }
  }, [openAirportDetails, jobDetails]);

  useEffect(() => {
    getTailFlightInfo();
  }, []);

  const handleToggleAssignedPmPriceVisibility = async () => {
    const assignedPm = priceBreakdownComparison?.assigned_project_manager_info;

    if (!assignedPm?.id) return;

    const newValue = !assignedPm.show_price_breakdown;

    try {
      setSavingAssignedPmPriceVisibility(true);

      const request = {
        show_job_price: newValue,
      };

      await api.updateUser(assignedPm.id, request);

      setPriceBreakdownComparison((prev) => ({
        ...prev,
        assigned_project_manager_info: {
          ...prev.assigned_project_manager_info,
          show_price_breakdown: newValue,
        },
      }));

      toast.success("Project manager price visibility updated.");
    } catch (err) {
      toast.error("Unable to update project manager price visibility.");
    } finally {
      setSavingAssignedPmPriceVisibility(false);
    }
  };

  const handleToggleJobCompleteModal = () => {
    setCompleteJobModalOpen(!isCompleteJobModalOpen);
  };

  const handleToggleInitialInspectionModal = () => {
    setInitialInspectionModalOpen(!isInitialInspectionModalOpen);
  };

  const handleToggleInspectionChecklistModal = (type) => {
    setInspectionType(type);
    setInspectionChecklistModalOpen(!isInspectionChecklistModalOpen);
  };

  const handleToggleJobCancelModal = () => {
    setIsCancelJobModalOpen(!isCancelJobModalOpen);
  };

  const handleTogglePriceBreakdownModal = () => {
    setPriceBreakdownModalOpen(!isPriceBreakdownModalOpen);
  };

  const handleToggleJobReturnModal = () => {
    setReturnJobModalOpen(!isReturnJobModalOpen);
  };

  const handleToggleJobFileUploadModal = () => {
    setJobFileUploadModalOpen(!isJobFileUploadModalOpen);
  };

  const handleFboChange = (e) => {
    const { name, value } = e.target;
    setFboForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAirportChange = (e) => {
    const { name, value } = e.target;
    setAirportForm((prev) => ({ ...prev, [name]: value }));
  };

  const getTailFlightInfo = async () => {
    try {
      const { data } = await api.getTailFlightInfo(jobId);

      setFlights(data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSaveAirport = async (values = airportForm) => {
    setSavingAirport(true);
    try {
      const payload = {
        is_wet_wash_allowed: values.is_wet_wash_allowed,
        special_notes: values.special_notes?.trim() || null,
      };

      await api.updateAirportDetails(jobDetails.airport.id, payload);

      // optimistic update of local jobDetails
      setJobDetails((prev) => ({
        ...prev,
        airport: { ...prev.airport, ...payload },
      }));

      toast.success("Airport details updated.");
      setOpenAirportDetails(false);
      return true;
    } catch (err) {
      toast.error("Unable to update airport details.");
      return false;
    } finally {
      setSavingAirport(false);
    }
  };

  const handleSaveFbo = async (values = fboForm) => {
    setSavingFbo(true);
    try {
      const payload = {
        hours_of_operation: values.hours_of_operation?.trim() || null,
        phone_number: values.phone_number?.trim() || null,
        website: values.website?.trim() || null,
        special_notes: values.special_notes?.trim() || null,
      };

      await api.updateFboDetails(jobDetails.fbo.id, payload);

      // optimistic update of local jobDetails
      setJobDetails((prev) => ({
        ...prev,
        fbo: { ...prev.fbo, ...payload },
      }));

      toast.success("FBO details updated.");
      setOpenFboDetails(false);
      return true;
    } catch (err) {
      toast.error("Unable to update FBO details.");
      return false;
    } finally {
      setSavingFbo(false);
    }
  };

  const getJobDetails = async () => {
    setLoading(true);

    let jobCurrentStatus = "";

    try {
      const { data } = await api.getJobDetails(jobId);

      const vendorAcceptedTag = data.tags.find(
        (tag) => tag.tag_name === "Vendor Accepted",
      );

      const acceptedTag = data.tags.find((tag) => tag.tag_name === "Accepted");

      if (vendorAcceptedTag || acceptedTag) {
        setIsVendorAccepted(true);
      }

      jobCurrentStatus = data.status;

      for (let i = 0; i < data.service_assignments.length; i++) {
        const service = data.service_assignments[i];
        if (service.vendor) {
          setVendorForm(service.vendor);
          break;
        }
      }
      for (let i = 0; i < data.retainer_service_assignments.length; i++) {
        const service = data.retainer_service_assignments[i];
        if (service.vendor) {
          setVendorForm(service.vendor);
          break;
        }
      }

      setJobDetails(data);

      setLoading(false);

      const response = await api.getUserDetails();

      if (
        response.data.isAdmin ||
        response.data.isSuperUser ||
        response.data.isAccountManager ||
        response.data.isInternalCoordinator ||
        response.data.isCustomer
      ) {
        try {
          const formResponse = await api.getJobFormInfo();
          setJobFormOptions(formResponse.data);
        } catch (err) {
          toast.error("Unable to load job editing options.");
        }
      }

      if (
        response.data.isAdmin ||
        response.data.isSuperUser ||
        response.data.isAccountManager
      ) {
        try {
          const r = await api.getJobInvoiceDetails(Number(jobId));
          setInvoiceDetails(r.data);
        } catch (err) {
          toast.error("Unable to get job invoice details.");
        }

        try {
          const identResponse = await api.getIdentTailLookup(data.tailNumber);
          setCallSign(identResponse.data || "");
        } catch (err) {
          setCallSign("");
        }
      }

      if (
        response.data.isAdmin ||
        response.data.isSuperUser ||
        response.data.isAccountManager ||
        response.data.isInternalCoordinator
      ) {
        setIsAdmin(true);

        const request = {
          tail_number: data.tailNumber,
        };

        const response2 = await api.getTailServiceHistory(request);

        const uniqueServiceActivities = [];

        for (let i = 0; i < response2.data.results.length; i++) {
          const serviceActivity = response2.data.results[i];
          const found = uniqueServiceActivities.some(
            (el) =>
              el.service_name === serviceActivity.service_name &&
              el.purchase_order === serviceActivity.purchase_order,
          );

          if (!found) {
            uniqueServiceActivities.push(serviceActivity);
          }
        }

        setServiceActivities(uniqueServiceActivities);
      }

      if (
        response.data.isAdmin ||
        response.data.isSuperUser ||
        response.data.isAccountManager ||
        (response.data.isInternalCoordinator && response.data.canSeePrice)
      ) {
        const { data } = await api.getJobPriceBreakdownComparison(jobId);

        setPriceBreakdownComparison(data);
      }
    } catch (error) {
      setLoading(false);

      if (error.response?.status === 403) {
        setErrorMessage("You do not have permission to view this job.");
      } else {
        setErrorMessage("Unable to load job details.");
      }
    }

    try {
      const { data } = await api.getJobPriceBreakdown(jobId);

      setPriceBreakdown(data);
    } catch (err) {
      console.log(err);
    }

    try {
      const { data } = await api.getJobActivities(jobId);

      //get the data.results where the activity_type is "S"
      const statusActivities = data.results.filter(
        (s) => s.activity_type === "S",
      );
      const isSubmitted = statusActivities.some((s) => s.status === "U");
      const isAccepted = statusActivities.some((s) => s.status === "A");
      let isAssigned = statusActivities.some((s) => s.status === "S");
      const isWIP = statusActivities.some((s) => s.status === "W");
      const isCompleted = statusActivities.some((s) => s.status === "C");
      const isInvoiced = statusActivities.some((s) => s.status === "I");

      let statusSteps = [
        { name: "Received", status: "upcoming", selected: false },
        { name: "Assigned", status: "upcoming", selected: false },
        { name: "WIP", status: "upcoming", selected: false },
        { name: "Completed", status: "upcoming", selected: false },
        { name: "Invoiced", status: "upcoming", selected: false },
      ];

      //if currentUser.canConfirmJobs is true, then Received in statusSteps should be name Confirmed
      if (currentUser.canConfirmJobs) {
        statusSteps = statusSteps.map((s) => {
          if (s.name === "Received") {
            s = { ...s, name: "Confirmed" };
          }
          return s;
        });
      }

      if (isSubmitted) {
        //add the submitted step to the beginning of the steps array
        statusSteps.unshift({
          name: "Submitted",
          status: "complete",
          selected: false,
        });
      }

      //if isInvoiced is true, all steps should have status complete and selected false
      if (isInvoiced) {
        statusSteps = statusSteps.map((s) => {
          s = { ...s, status: "complete", selected: false };
          return s;
        });
      } else if (isCompleted) {
        // all the steps except the last one should have status complete and selected false
        statusSteps = statusSteps.map((s, index) => {
          if (index < statusSteps.length - 1) {
            s = { ...s, status: "complete", selected: false };
          }
          return s;
        });
      } else if (isWIP) {
        // all the steps except the last two should have status complete and selected false
        statusSteps = statusSteps.map((s, index) => {
          if (index < statusSteps.length - 3) {
            s = { ...s, status: "complete", selected: false };
          }
          return s;
        });

        //search for step with name WIP and set status to current
        statusSteps = statusSteps.map((s) => {
          if (s.name === "WIP") {
            s = { ...s, status: "current", selected: true };
          }
          return s;
        });
      } else if (
        isAssigned &&
        (jobCurrentStatus === "S" ||
          jobCurrentStatus === "W" ||
          jobCurrentStatus === "C" ||
          jobCurrentStatus === "I")
      ) {
        // all the steps except the last three should have status complete and selected false
        statusSteps = statusSteps.map((s, index) => {
          if (index < statusSteps.length - 3) {
            s = { ...s, status: "complete", selected: false };
          }
          return s;
        });
      } else if (isAccepted) {
        // all the steps except the last four should have status complete and selected false
        statusSteps = statusSteps.map((s, index) => {
          if (index < statusSteps.length - 4) {
            s = { ...s, status: "complete", selected: false };
          }
          return s;
        });
      } else if (isSubmitted) {
        // all the steps except the last five should have status complete and selected false
        statusSteps = statusSteps.map((s, index) => {
          if (index < statusSteps.length - 5) {
            s = { ...s, status: "complete", selected: false };
          }
          return s;
        });
      }

      setSteps(statusSteps);
    } catch (err) {
      toast.error("Unable to get job status");
    }

    try {
      const r = {
        jobId: jobId,
      };

      const { data } = await api.getTailNoteLookup(r);

      setTailDetailsFound(true);
      setTailDetailsId(data.id);
      setTailDetails(data);
    } catch (err) {
      setTailDetailsFound(false);
      setTailDetailsId(null);
      setTailDetails(null);
    }
  };

  const handleToggleWetWashAllowed = (checked) => {
    setAirportForm((prev) => ({
      ...prev,
      is_wet_wash_allowed: checked,
    }));
  };

  const completeJob = async (
    status,
    hoursWorked,
    minutesWorked,
    numberOfWorkers,
    laborTime,
    userSelected,
    inspectionPerformedBy,
    jobCommentId,
  ) => {
    setCompleteJobModalOpen(false);
    setIsCancelJobModalOpen(false);

    setLoading(true);

    const request = {
      status,
      hours_worked: hoursWorked,
      minutes_worked: minutesWorked,
      number_of_workers: numberOfWorkers,
      labor_time: laborTime,
      user_selected_id: userSelected ? userSelected.id : null,
      final_inspection_done_by: inspectionPerformedBy,
      final_concern_comment_id: jobCommentId,
    };

    try {
      await api.completeJob(jobId, request);

      const updatedJobDetails = {
        ...jobDetails,
        status,
        service_assignments: jobDetails.service_assignments?.map((s) => {
          s = { ...s, status };
          return s;
        }),
        retainer_service_assignments:
          jobDetails.retainer_service_assignments?.map((s) => {
            s = { ...s, status };
            return s;
          }),
      };

      setJobDetails(updatedJobDetails);

      setLoading(false);

      navigate("/jobs");
    } catch (e) {
      toast.error("Unable to update job status.");
      setLoading(false);
    }
  };

  const handleAcceptJob = async () => {
    try {
      await api.acceptJob(jobId);

      setIsVendorAccepted(true);
      navigate(0);
    } catch (e) {
      toast.error("Unable to accept job.");
    }
  };

  const updateJobStatus = async (status) => {
    setCompleteJobModalOpen(false);
    setIsCancelJobModalOpen(false);

    setLoading(true);

    try {
      await api.updateJobStatus(jobId, status);

      const updatedJobDetails = {
        ...jobDetails,
        status,
        service_assignments: jobDetails.service_assignments?.map((s) => {
          s = { ...s, status };
          return s;
        }),
        retainer_service_assignments:
          jobDetails.retainer_service_assignments?.map((s) => {
            s = { ...s, status };
            return s;
          }),
      };

      setJobDetails(updatedJobDetails);

      setLoading(false);

      toast.success("Job updated!.");

      if (status === "C") {
        navigate("/jobs");
      } else {
        navigate(0);
      }
    } catch (e) {
      toast.error("Unable to update job status.");
      setLoading(false);
    }
  };

  const returnJob = async (comment) => {
    setReturnJobModalOpen(false);

    setLoading(true);

    const request = {
      comment,
    };

    try {
      await api.returnJob(jobId, request);

      navigate("/jobs");
    } catch (error) {
      toast.error("Unable to return job.");
      setLoading(false);
    }
  };

  const completeService = async (service_assignment_id) => {
    try {
      const { data } = await api.completeServiceAssignment(
        service_assignment_id,
      );

      const updatedJobDetails = {
        ...jobDetails,
        service_assignments: jobDetails.service_assignments?.map((service) => {
          if (service.id === service_assignment_id) {
            service = { ...service, status: "C" };
          }

          return service;
        }),
      };

      setJobDetails(updatedJobDetails);

      if (data.can_complete_job) {
        setCompleteJobModalOpen(true);
      }
    } catch (e) {}
  };

  const completeRetainerService = async (retainer_service_assignment_id) => {
    try {
      const { data } = await api.completeRetainerServiceAssignment(
        retainer_service_assignment_id,
      );

      const updatedJobDetails = {
        ...jobDetails,
        retainer_service_assignments:
          jobDetails.retainer_service_assignments?.map((service) => {
            if (service.id === retainer_service_assignment_id) {
              service = { ...service, status: "C" };
            }

            return service;
          }),
      };

      setJobDetails(updatedJobDetails);

      if (data.can_complete_job) {
        setCompleteJobModalOpen(true);
      }
    } catch (e) {}
  };

  const removeService = async (service) => {
    await api.deleteService(service.id);

    const updatedJobDetails = {
      ...jobDetails,
      service_assignments: jobDetails.service_assignments?.filter(
        (s) => s.id !== service.id,
      ),
    };

    toast.success("Service removed!.");

    setJobDetails(updatedJobDetails);
  };

  const removeRetainerService = async (service) => {
    await api.deleteRetainerService(service.id);

    const updatedJobDetails = {
      ...jobDetails,
      retainer_service_assignments:
        jobDetails.retainer_service_assignments?.filter(
          (s) => s.id !== service.id,
        ),
    };

    toast.success("Retainer removed!.");

    setJobDetails(updatedJobDetails);
  };

  const handleToggleAddServiceModal = () => {
    setAddServiceModalOpen(!isAddServiceModalOpen);
  };

  const handleToggleCategoriesModal = () => {
    setCategoriesModalOpen(!isCategoriesModalOpen);
  };

  const handleToggleAddRetainerServiceModal = () => {
    setAddRetainerServiceModalOpen(!isAddRetainerServiceModalOpen);
  };

  const handleToggleJobInvoiceModal = () => {
    setJobInvoiceModalOpen(!isJobInvoiceModalOpen);
  };

  const handleToggleJobNotInvoiceModal = () => {
    setJobNotInvoiceModalOpen(!isJobNotInvoiceModalOpen);
  };

  const handleAddService = (updatedServices) => {
    setAddServiceModalOpen(false);

    //refetch the job details because you have to rebuild all services
    getJobDetails();
  };

  const handleUpdateCategories = (categories) => {
    setCategoriesModalOpen(false);

    const updatedJobDetails = {
      ...jobDetails,
      categories: categories,
    };

    setJobDetails(updatedJobDetails);
  };

  const handleAddRetainerService = (updatedServices) => {
    setAddRetainerServiceModalOpen(false);

    //refetch the job details because you have to rebuild all services
    getJobDetails();
  };

  const startJob = async (inspectionPerformedBy, initial_issue_comment_id) => {
    setInitialInspectionModalOpen(false);
    let status = "W";
    setLoading(true);

    const request = {
      status: "W",
      inspection_performed_by: inspectionPerformedBy,
      initial_issue_comment_id: initial_issue_comment_id,
    };

    try {
      await api.startJob(jobId, request);

      const updatedJobDetails = {
        ...jobDetails,
        status,
        service_assignments: jobDetails.service_assignments?.map((s) => {
          s = { ...s, status };
          return s;
        }),
        retainer_service_assignments:
          jobDetails.retainer_service_assignments?.map((s) => {
            s = { ...s, status };
            return s;
          }),
      };

      setJobDetails(updatedJobDetails);

      toast.success("Job updated!.");

      navigate(0);
    } catch (e) {
      toast.error("Unable to update job status.");
    } finally {
      setLoading(false);
    }
  };

  const addJobFile = (file) => {
    const updatedJobDetails = {
      ...jobDetails,
      files: [...jobDetails.files, file],
    };

    setJobDetails(updatedJobDetails);
    setJobFileUploadModalOpen(false);
    toast.success("File uploaded successfully.");
  };

  const downloadFile = (file) => {
    const fileUrl =
      "https://res.cloudinary.com/datidxeqm/" + file.file + "?dl=true";
    window.open(fileUrl, "_blank");
  };

  const handleToggleFilePublic = async (file) => {
    const request = {
      is_public: !file.is_public,
    };

    try {
      await api.updateJobFile(file.id, request);

      const updatedJobDetails = {
        ...jobDetails,
        files: jobDetails.files.map((f) => {
          if (f.id === file.id) {
            f = { ...f, is_public: !f.is_public };
          }

          return f;
        }),
      };

      setJobDetails(updatedJobDetails);
    } catch (err) {
      toast.error("Unable to update file privacy.");
    }
  };

  const handleDeleteJobFile = async (file) => {
    try {
      await api.deleteJobFile(file.id);

      const updatedJobDetails = {
        ...jobDetails,
        files: jobDetails.files.filter((f) => f.id !== file.id),
      };

      setJobDetails(updatedJobDetails);

      toast.success("File deleted!.");
    } catch (err) {
      toast.error("Unable to delete file.");
    }
  };

  const handleSaveVendor = async (values = vendorForm) => {
    setSavingVendor(true);
    try {
      const payload = {
        name: vendorForm.name?.trim() || null,
        phoneNumbers: values.phone_numbers?.trim() || null,
        emails: values.emails?.trim() || null,
        billingAddress: values.billing_address?.trim() || null,
        notes: values.notes?.trim() || null,
      };

      await api.updateVendorDetails(vendorForm.id, payload);

      setVendorForm((prev) => ({ ...prev, ...values }));
      toast.success("Vendor details updated.");
      setOpenVendorDetails(false);
      return true;
    } catch (err) {
      toast.error("Unable to update Vendor details.");
      return false;
    } finally {
      setSavingVendor(false);
    }
  };

  const handleVendorChange = (e) => {
    const { name, value } = e.target;
    setVendorForm((prev) => ({ ...prev, [name]: value }));
  };

  const savePricingRow = async (payload) => {
    const { data } = await api.updateJobPricingRow(Number(jobId), payload);
    setPriceBreakdownComparison(data);
    setPriceBreakdown(data.customerPriceBreakdown);
    setJobDetails((current) => ({
      ...current,
      price: Object.keys(payload.changes || {}).length === 1 && "scope" in payload.changes
        ? current.price : data.customerPriceBreakdown.totalPrice.replace(/,/g, ""),
      service_assignments: current.service_assignments.map((service) => {
        const row = data.customerPriceBreakdown.services.find((row) => row.id === service.service_id);
        return {
          ...service,
          name: row?.displayName || service.name,
          ...(row?.scope !== undefined ? { scope: row.scope } : {}),
        };
      }),
    }));
    return data;
  };

  const saveInlineJobSection = async (
    section,
    payload,
    successMessage,
    refreshInvoice = false,
  ) => {
    setSavingJobSection(section);
    try {
      await api.updateJob(jobId, payload);

      const { data } = await api.getJobDetails(jobId);
      setJobDetails(data);
      setIsVendorAccepted(
        data.tags.some(
          (tag) =>
            tag.tag_name === "Vendor Accepted" ||
            tag.tag_name === "Accepted",
        ),
      );

      if (Object.prototype.hasOwnProperty.call(payload, "ident")) {
        setCallSign(payload.ident || "");
      }

      if (refreshInvoice) {
        const invoiceResponse = await api.getJobInvoiceDetails(Number(jobId));
        setInvoiceDetails(invoiceResponse.data);
      }

      toast.success(successMessage);
      return true;
    } catch (err) {
      toast.error("Unable to update job details.");
      return false;
    } finally {
      setSavingJobSection(null);
    }
  };

  const handleSearchAirports = async (query) => {
    try {
      const { data } = await api.searchAirports({ name: query });
      return data.results || [];
    } catch (err) {
      toast.error("Unable to search airports.");
      return [];
    }
  };

  const handleSearchFbos = async (query, airportId) => {
    try {
      const { data } = await api.searchFbos({
        name: query,
        airport_id: airportId,
      });
      return data.results || [];
    } catch (err) {
      toast.error("Unable to search FBOs.");
      return [];
    }
  };

  const handleSearchAircraftTypes = async (query) => {
    try {
      const { data } = await api.searchAircraftTypes({ name: query });
      return data.results || [];
    } catch (err) {
      toast.error("Unable to search aircraft types.");
      return [];
    }
  };

  const handleSearchCustomers = async (query) => {
    try {
      const { data } = await api.getCustomers({ name: query });
      return data.results || [];
    } catch (err) {
      toast.error("Unable to search customers.");
      return [];
    }
  };

  const handleSearchVendors = async (query) => {
    try {
      const { data } = await api.searchVendors({ name: query });
      return data.results || [];
    } catch (err) {
      toast.error("Unable to search vendors.");
      return [];
    }
  };

  const handleLoadCustomerFollowerEmails = async (customerId) => {
    try {
      const { data } = await api.getCustomerFollowerEmails(customerId);
      return data.results || [];
    } catch (err) {
      toast.error("Unable to load customer follower emails.");
      throw err;
    }
  };

  return (
    <div>
      {loading && <Loader />}
      {!loading && errorMessage && (
        <div className="text-gray-500 m-auto text-center mt-20">
          {errorMessage}
        </div>
      )}
      {!loading &&
        errorMessage == null &&
        (isJobDetailsRoute || isCompletedReview ? (
          <JobDetailsOverview
            isCompletedReview={isCompletedReview}
            reviewActions={reviewActions}
            reviewMenu={reviewMenu}
            jobId={jobId}
            job={jobDetails}
            user={currentUser}
            steps={steps}
            tailDetailsFound={tailDetailsFound}
            tailDetailsId={tailDetailsId}
            tailDetails={tailDetails}
            priceBreakdown={priceBreakdown}
            priceBreakdownComparison={priceBreakdownComparison}
            invoiceDetails={invoiceDetails}
            formOptions={jobFormOptions}
            callSign={callSign}
            vendor={vendorForm}
            flights={flights}
            serviceActivities={serviceActivities}
            isAdmin={isAdmin}
            isVendorAccepted={isVendorAccepted}
            actions={{
              updateJobStatus,
              acceptJob: handleAcceptJob,
              openInitialInspection: handleToggleInitialInspectionModal,
              openCompleteJob: handleToggleJobCompleteModal,
              openInvoice: handleToggleJobInvoiceModal,
              openNotInvoice: handleToggleJobNotInvoiceModal,
              openReturnJob: handleToggleJobReturnModal,
              openCancelJob: handleToggleJobCancelModal,
              openAirportDetails: () => setOpenAirportDetails(true),
              openFboDetails: () => setOpenFboDetails(true),
              openVendorDetails: () => setOpenVendorDetails(true),
              openCategories: handleToggleCategoriesModal,
              openInspection: handleToggleInspectionChecklistModal,
              openPriceBreakdown: handleTogglePriceBreakdownModal,
              togglePmPriceVisibility: handleToggleAssignedPmPriceVisibility,
              savingPmPriceVisibility: savingAssignedPmPriceVisibility,
              saveAirportDetails: handleSaveAirport,
              savingAirportDetails: savingAirport,
              saveFboDetails: handleSaveFbo,
              savingFboDetails: savingFbo,
              saveVendorDetails: handleSaveVendor,
              savingVendorDetails: savingVendor,
              openAddService: handleToggleAddServiceModal,
              openAddRetainerService: handleToggleAddRetainerServiceModal,
              removeService,
              removeRetainerService,
              completeService,
              completeRetainerService,
              openFileUpload: handleToggleJobFileUploadModal,
              deleteFile: handleDeleteJobFile,
              downloadFile,
              toggleFilePublic: handleToggleFilePublic,
              savePricingRow,
              saveOperationalDetails: (payload) =>
                saveInlineJobSection(
                  "operational",
                  payload,
                  "Operational details updated.",
                ),
              saveJobInformation: (payload) =>
                saveInlineJobSection(
                  "job-information",
                  payload,
                  "Job information updated.",
                ),
              saveFollowers: (followerEmails) =>
                saveInlineJobSection(
                  "followers",
                  { followerEmails },
                  "Followers updated.",
                ),
              saveInvoiceDetails: (payload) =>
                saveInlineJobSection(
                  "invoice",
                  payload,
                  "Invoice details updated.",
                  true,
                ),
              saveLaborDetails: (payload) =>
                saveInlineJobSection(
                  "labor",
                  payload,
                  "Labor details updated.",
                ),
              saveTags: (tags) =>
                saveInlineJobSection(
                  "tags",
                  { tags },
                  "Job tags updated.",
                ),
              searchAirports: handleSearchAirports,
              searchFbos: handleSearchFbos,
              searchAircraftTypes: handleSearchAircraftTypes,
              searchCustomers: handleSearchCustomers,
              searchVendors: handleSearchVendors,
              loadCustomerFollowerEmails: handleLoadCustomerFollowerEmails,
              savingJobSection,
            }}
          />
        ) : (
          <div className="mt-6 w-full px-2">
            <div className="flex flex-wrap justify-between gap-y-4 gap-x-14">
              <div className=" w-72">
                <h1 className="text-xl xl:text-2xl font-bold text-gray-700 flex flex-wrap">
                  {jobDetails.tailNumber}
                  {tailDetailsFound && (
                    <Link
                      to={`/jobs/${tailDetailsId}/tail-details`}
                      className="text-sky-600 ml-2 font-semibold cursor-pointer text-xl flex gap-1"
                    >
                      key location
                      <ArrowRightIcon
                        className="h-5 w-5 relative"
                        style={{ top: "3px" }}
                      />
                    </Link>
                  )}
                </h1>
                {!currentUser.isProjectManager && (
                  <div className="mt-1 text-md">
                    {jobDetails.integration_customer
                      ? jobDetails.integration_customer.name
                      : jobDetails.customer?.name}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div
                  className={`xs:block sm:block xl:hidden lg:hidden md:hidden text-lg inline-block rounded-md px-2 py-1 mr-2 border mb-2 mt-2
                                        ${
                                          jobDetails.status === "A" &&
                                          "border-blue-500 text-blue-500"
                                        }
                                        ${
                                          jobDetails.status === "S" &&
                                          "border-yellow-500 text-yellow-500"
                                        }
                                        ${
                                          jobDetails.status === "U" &&
                                          "border-indigo-500 text-indigo-500"
                                        }
                                        ${
                                          jobDetails.status === "W" &&
                                          "border-green-500 text-green-500"
                                        }
                                        ${
                                          jobDetails.status === "R" &&
                                          "border-purple-500 text-purple-500"
                                        }
                                        ${
                                          jobDetails.status === "C" &&
                                          "border-green-500 text-green-500"
                                        }
                                        ${
                                          jobDetails.status === "I" &&
                                          "border-blue-500 text-blue-500"
                                        }
                                        ${
                                          jobDetails.status === "T" &&
                                          "border-gray-700 text-gray-700"
                                        }`}
                >
                  {jobDetails.status === "A" && "Received"}
                  {jobDetails.status === "S" && "Assigned"}
                  {jobDetails.status === "U" && "Submitted"}
                  {jobDetails.status === "W" && "WIP"}
                  {jobDetails.status === "C" && "Complete"}
                  {jobDetails.status === "T" && "Canceled"}
                  {jobDetails.status === "R" && "Review"}
                  {jobDetails.status === "I" && "Invoiced"}
                </div>

                {jobDetails.status !== "T" && (
                  <nav
                    aria-label="Progress"
                    className="hidden md:block lg:block xl:block"
                  >
                    <ol className="divide-y divide-gray-300 rounded-md border border-gray-300 md:flex md:divide-y-0">
                      {steps.map((step, stepIdx) => (
                        <li
                          key={stepIdx}
                          className="relative md:flex md:flex-1"
                        >
                          {step.status === "complete" ? (
                            <button className="group flex w-full items-center">
                              <span className="flex items-center py-2 px-6 text-sm font-medium">
                                <span
                                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center
                                                rounded-full bg-green-500 group-hover:bg-green-700"
                                >
                                  <CheckIcon
                                    className="h-6 w-6 text-white"
                                    aria-hidden="true"
                                  />
                                </span>
                                <span className="ml-2 text-md font-medium text-gray-900">
                                  {step.name}
                                </span>
                              </span>
                            </button>
                          ) : step.status === "current" ? (
                            <button
                              className="flex items-center py-2 px-6 text-sm font-medium"
                              aria-current="step"
                            >
                              <span
                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center
                                                        rounded-full border-2 border-blue-600"
                              >
                                <span className="text-blue-600">
                                  {stepIdx + 1}
                                </span>
                              </span>
                              <span className="ml-2 text-md font-medium text-blue-600">
                                {step.name}
                              </span>
                            </button>
                          ) : (
                            <button className="group flex items-center">
                              <span className="flex items-center py-2 px-6 text-sm font-medium">
                                <span
                                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full
                                                            border-2 border-gray-300 group-hover:border-gray-400"
                                >
                                  <span className="text-gray-500 group-hover:text-gray-900">
                                    {stepIdx + 1}
                                  </span>
                                </span>
                                <span className="ml-2 text-md font-medium text-gray-500 group-hover:text-gray-900">
                                  {step.name}
                                </span>
                              </span>
                            </button>
                          )}

                          {stepIdx !== steps.length - 1 ? (
                            <>
                              <div
                                className="absolute top-0 right-0 hidden h-full w-5 md:block"
                                aria-hidden="true"
                              >
                                <svg
                                  className="h-full w-full text-gray-300"
                                  viewBox="0 0 22 80"
                                  fill="none"
                                  preserveAspectRatio="none"
                                >
                                  <path
                                    d="M0 -2L20 40L0 82"
                                    vectorEffect="non-scaling-stroke"
                                    stroke="currentcolor"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  </nav>
                )}

                {jobDetails.status === "T" && (
                  <div className="hidden md:block lg:block xl:block bg-gray-700 mt-1 text-xl text-white rounded-md py-2 px-4 w-32">
                    Cancelled
                  </div>
                )}
              </div>
              <div className="">
                {currentUser.isCustomer &&
                  (jobDetails.status === "U" || jobDetails.status === "A") && (
                    <div className="flex flex-wrap gap-4">
                      {jobDetails.status === "U" &&
                        currentUser.canConfirmJobs && (
                          <button
                            type="button"
                            onClick={() => updateJobStatus("A")}
                            className="inline-flex items-center justify-center rounded-md
                                                    border border-transparent bg-red-600 px-4 py-2 text-lg
                                                    font-bold text-white shadow-sm hover:bg-red-700
                                                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                          >
                            Approve Job
                          </button>
                        )}
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/jobs/${jobDetails.id}/customer-edit`)
                        }
                        className="items-center rounded-md border
                                                    border-gray-300 bg-white px-4 py-2 text-lg font-normal
                                                    text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none
                                                    focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleJobCancelModal()}
                        className="items-center rounded-md border
                                                    border-gray-300 bg-white px-4 py-2 text-lg font-normal
                                                    text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none
                                                    focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                {!currentUser.isCustomer && (
                  <div className="text-left lg:text-right">
                    {jobDetails.status === "S" && (
                      <div className="flex gap-4">
                        {currentUser.isProjectManager && (
                          <>
                            {currentUser.canAcceptJobs && (
                              <>
                                {isVendorAccepted && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleToggleInitialInspectionModal()
                                    }
                                    className="inline-flex items-center justify-center rounded-md
                                                              border border-transparent bg-red-600 px-4 py-2 text-lg
                                                              font-bold text-white shadow-sm hover:bg-red-700
                                                              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                                  >
                                    Start Job
                                  </button>
                                )}
                                {!isVendorAccepted && (
                                  <button
                                    type="button"
                                    onClick={() => handleAcceptJob()}
                                    className="rounded bg-white px-4 py-2 text-lg text-gray-900
                                  shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                  >
                                    Accept Job
                                  </button>
                                )}
                              </>
                            )}
                            {!currentUser.canAcceptJobs && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleInitialInspectionModal()
                                }
                                className="inline-flex items-center justify-center rounded-md
                                                      border border-transparent bg-red-600 px-4 py-2 text-lg
                                                      font-bold text-white shadow-sm hover:bg-red-700
                                                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                              >
                                Start Job
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleToggleJobReturnModal()}
                              className="rounded bg-white px-4 py-2 text-lg text-gray-900
                                                            shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                              Return Job
                            </button>
                          </>
                        )}
                        {!currentUser.isProjectManager && (
                          <button
                            type="button"
                            onClick={() => handleToggleInitialInspectionModal()}
                            className="inline-flex items-center justify-center rounded-md
                                                    border border-transparent bg-red-600 px-4 py-2 text-lg
                                                    font-bold text-white shadow-sm hover:bg-red-700
                                                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                          >
                            Start Job
                          </button>
                        )}
                      </div>
                    )}

                    {jobDetails.status === "U" && (
                      <button
                        type="button"
                        onClick={() => updateJobStatus("A")}
                        className="inline-flex items-center justify-center rounded-md
                                                border border-transparent bg-red-600 px-4 py-2 text-lg
                                                font-bold text-white shadow-sm hover:bg-red-700
                                                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                      >
                        Receive Job
                      </button>
                    )}

                    {(jobDetails.status === "A" || currentUser.isMasterPM) && (
                      <button
                        type="button"
                        onClick={() => navigate(`/jobs/${jobId}/assignments`)}
                        className="inline-flex items-center justify-center rounded-md
                                                border border-transparent bg-red-600 px-4 py-2 text-lg
                                                font-bold text-white shadow-sm hover:bg-red-700
                                                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto mr-2"
                      >
                        Assign Job
                      </button>
                    )}

                    {jobDetails.status === "W" && (
                      <button
                        type="button"
                        onClick={() => handleToggleJobCompleteModal()}
                        className="inline-flex items-center justify-center rounded-md
                                                border border-transparent bg-red-500 px-4 py-2 text-lg
                                                font-medium text-white shadow-sm hover:bg-red-700
                                                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                      >
                        Complete Job
                      </button>
                    )}

                    {jobDetails.status === "C" &&
                      currentUser.enableInvoices &&
                      (currentUser.isAdmin ||
                        currentUser.isSuperUser ||
                        currentUser.isAccountManager) && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleToggleJobInvoiceModal()}
                            className="inline-flex items-center justify-center rounded-md
                                                      border border-transparent bg-red-500 px-4 py-2 text-lg
                                                      font-medium text-white shadow-sm hover:bg-red-700
                                                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                          >
                            Invoice Job
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggleJobNotInvoiceModal()}
                            className="inline-flex items-center rounded-md border ml-2
                                     border-gray-300 bg-white px-4 py-2 text-lg font-medium
                                     text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2
                                      focus:ring-gray-500 focus:ring-offset-2"
                          >
                            Not Invoice Job
                          </button>
                        </>
                      )}
                  </div>
                )}
              </div>
            </div>
            {jobDetails.tags?.map((tag) => (
              <div
                key={tag.id}
                className={`text-lg inline-block rounded-md px-2 py-1 mr-2 border mb-2 mt-4
                            ${
                              tag.tag_color === "red" &&
                              "border-red-500 text-red-500"
                            }
                            ${
                              tag.tag_color === "orange" &&
                              "border-orange-500 text-orange-500 "
                            }
                            ${
                              tag.tag_color === "amber" &&
                              "border-amber-500 text-amber-500"
                            }
                            ${
                              tag.tag_color === "indigo" &&
                              " border-indigo-500 text-indigo-500"
                            }
                            ${
                              tag.tag_color === "violet" &&
                              " border-violet-500 text-violet-500"
                            }
                            ${
                              tag.tag_color === "fuchsia" &&
                              "border-fuchsia-500 text-fuchsia-500"
                            } 
                            ${
                              tag.tag_color === "pink" &&
                              "border-pink-500 text-pink-500"
                            }
                            ${
                              tag.tag_color === "slate" &&
                              "border-slate-500 text-gray-500"
                            }
                            ${
                              tag.tag_color === "lime" &&
                              "border-lime-500 text-lime-500"
                            }
                            ${
                              tag.tag_color === "emerald" &&
                              "border-emerald-500 text-emerald-500"
                            }
                            ${
                              tag.tag_color === "cyan" &&
                              "border-cyan-500 text-cyan-500"
                            }
                            ${
                              tag.tag_color === "blue" &&
                              "border-blue-500 text-blue-500"
                            }
                        `}
              >
                {tag.tag_name}
              </div>
            ))}

            {(jobDetails.status === "C" || jobDetails.status === "I") && (
              <CustomerFeedback
                jobId={jobDetails.id}
                feedback_rating={jobDetails.feedback_rating}
                noFeedbackSubmittedYet={jobDetails.feedback_rating == null}
                currentUser={currentUser}
                feedback_author={jobDetails.feedback_author}
              />
            )}

            {jobDetails.show_billing_info && (
              <div className="bg-indigo-700 py-8 mt-4 rounded-md">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                  <h2 className="max-w-xl text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:col-span-7">
                    Billing Info
                  </h2>
                  <p
                    className="mt-4 text-md text-white"
                    dangerouslySetInnerHTML={{
                      __html: jobDetails?.customer?.billingInfo?.replace(
                        /\r\n|\n/g,
                        "<br />",
                      ),
                    }}
                  ></p>
                </div>
              </div>
            )}

            <div className="grid 3xl:grid-cols-4 2xl:grid-cols-3 xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 xs:grid-cols-1 mt-4 gap-6">
              {/* LOCATION AND TIMES */}
              <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                <div className="p-4 bg-gray-100">
                  <h3 className="text-base font-bold leading-7 text-gray-900 uppercase">
                    Location and Times
                  </h3>
                </div>
                <div className="border-t border-gray-200">
                  <dl className="divide-y divide-gray-100">
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Airport:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.airport?.name}
                        <div>
                          {jobDetails.airport?.timezone && (
                            <div className="rounded-md bg-blue-50 p-2 mt-1">
                              <div className="text-sm text-blue-700">
                                <ul className="list-disc space-y-1">
                                  <h3 className="text-sm font-medium text-blue-800">
                                    Timezone
                                  </h3>
                                  {jobDetails.airport?.timezone}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </dd>
                      <div className="ml-auto">
                        <button
                          type="button"
                          onClick={() => setOpenAirportDetails(true)}
                          className="inline-flex items-center rounded border
                                    border-sky-400 bg-white px-2 py-1 text-sm
                                    font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                    focus:outline-none cursor-pointer"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex gap-4 items-start">
                      <dt className="text-md font-bold text-gray-900">FBO:</dt>
                      <dd className="text-md text-gray-700 flex-1">
                        {jobDetails.fbo?.name}
                        <div>
                          {jobDetails.fbo?.hours_of_operation && (
                            <div className="rounded-md bg-red-50 p-2 mt-1">
                              <div className="text-sm text-red-700">
                                <ul className="list-disc space-y-1">
                                  <h3 className="text-sm font-medium text-red-800">
                                    Hours of operation
                                  </h3>
                                  {jobDetails.fbo?.hours_of_operation}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </dd>

                      <div className="ml-auto">
                        <button
                          type="button"
                          onClick={() => setOpenFboDetails(true)}
                          className="inline-flex items-center rounded border
                                    border-sky-400 bg-white px-2 py-1 text-sm
                                    font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                    focus:outline-none cursor-pointer"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Arrival:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.on_site
                          ? "On site"
                          : jobDetails.arrival_formatted_date}
                      </dd>
                    </div>
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Departure:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.departure_formatted_date}
                      </dd>
                    </div>
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Complete Before:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.completeBy ? (
                          jobDetails.complete_before_formatted_date
                        ) : (
                          <span
                            className="relative inline-flex items-center
                                                rounded-full border border-gray-300 px-2 py-0.5"
                          >
                            <div className="absolute flex flex-shrink-0 items-center justify-center">
                              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                            </div>
                            <div className="ml-3 text-md text-gray-700">
                              TBD
                            </div>
                          </span>
                        )}
                      </dd>
                    </div>
                    {jobDetails.completion_date && (
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Completed:
                        </dt>
                        <dd className="text-md text-gray-700">
                          {jobDetails.completion_date}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
              {/* JOB INFO */}
              <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                <div className="px-4 py-3 bg-gray-100">
                  <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                    Job Info
                  </h3>
                </div>
                <div className="border-t border-gray-200">
                  <dl className="divide-y divide-gray-100">
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Aircraft Type:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.aircraftType?.name}
                      </dd>
                    </div>
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Customer PO:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.customer_purchase_order
                          ? jobDetails.customer_purchase_order
                          : "Not provided"}
                      </dd>
                    </div>
                    {(currentUser.isAdmin ||
                      currentUser.isSuperUser ||
                      currentUser.isAccountManager ||
                      currentUser.isInternalCoordinator ||
                      currentUser.isCustomer) && (
                      <>
                        <div className="px-4 py-3 flex gap-4">
                          <dt className="text-md font-bold text-gray-900">
                            Requested By:
                          </dt>
                          <dd className="text-md text-gray-700">
                            {jobDetails.requested_by
                              ? jobDetails.requested_by
                              : jobDetails.created_by?.first_name +
                                " " +
                                jobDetails.created_by?.last_name}
                          </dd>
                        </div>
                        {jobDetails.is_publicly_confirmed && (
                          <>
                            <div className="px-4 py-3 flex gap-4">
                              <dt className="text-md font-bold text-gray-900">
                                Confirmed By:
                              </dt>
                              <dd className="text-md text-gray-700">
                                <div>{jobDetails.confirmed_full_name}</div>
                                <div className="mt-1">
                                  {jobDetails.confirmed_email}
                                </div>
                                <div className="mt-1">
                                  {jobDetails.confirmed_phone_number}
                                </div>
                              </dd>
                            </div>
                          </>
                        )}
                      </>
                    )}
                    <div className="px-4 py-3 flex gap-4">
                      <dt className="text-md font-bold text-gray-900">
                        Request Date:
                      </dt>
                      <dd className="text-md text-gray-700">
                        {jobDetails.requestDate}
                      </dd>
                    </div>

                    {(currentUser.isAdmin ||
                      currentUser.isSuperUser ||
                      currentUser.isAccountManager ||
                      currentUser.isInternalCoordinator ||
                      currentUser.isCustomer) && (
                      <>
                        <div className="px-4 py-3 flex flex-col">
                          <dt className="text-md flex justify-between gap-4">
                            <div className="font-bold text-gray-900">
                              Categories
                            </div>
                            <div>
                              <button
                                type="button"
                                onClick={handleToggleCategoriesModal}
                                className="inline-flex items-center rounded border
                                    border-sky-400 bg-white px-2 py-1 text-sm
                                    font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                    focus:outline-none cursor-pointer"
                              >
                                Manage
                              </button>
                            </div>
                          </dt>
                          <div className="py-3">
                            {jobDetails?.categories?.length ? (
                              <ul className="space-y-2">
                                {jobDetails.categories.map((category) => (
                                  <li key={category.id} className="w-full">
                                    <div className="w-full rounded-lg border border-gray-200 bg-white p-3 transition hover:shadow-sm">
                                      <div className="flex items-start gap-3">
                                        {/* Left: category name + meta */}
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {category.customer_category.name}
                                          </p>
                                          <p className="mt-0.5 text-xs text-gray-500">
                                            Added by{" "}
                                            {category.created_by.first_name}{" "}
                                            {category.created_by.last_name} ·{" "}
                                            <ReactTimeAgo
                                              date={
                                                new Date(category.created_at)
                                              }
                                              locale="en-US"
                                              timeStyle="twitter"
                                            />
                                          </p>
                                        </div>

                                        {/* Optional status dot (remove if not needed) */}
                                        <span
                                          className="mt-0.5 inline-flex h-2 w-2 flex-none rounded-full bg-emerald-500 ring-2 ring-white"
                                          aria-hidden="true"
                                          title="Active"
                                        />
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
                                <p className="text-sm text-gray-500">
                                  No categories added yet.
                                </p>
                              </div>
                            )}
                          </div>
                          {(jobDetails.inspection?.initial_inspection_done_by ||
                            jobDetails.inspection
                              ?.final_inspection_done_by) && (
                            <div className="mb-3">
                              <dt className="text-md flex justify-between gap-4">
                                <div className="font-bold text-gray-900">
                                  Inspections
                                </div>
                              </dt>
                              {jobDetails.inspection
                                .initial_inspection_done_by && (
                                <div className="flex justify-between mt-2 gap-2">
                                  <div>
                                    Initial done by:{" "}
                                    {
                                      jobDetails.inspection
                                        .initial_inspection_done_by
                                    }
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleToggleInspectionChecklistModal(
                                          "I",
                                        )
                                      }
                                      className="inline-flex items-center
                                                px-2 py-1 text-sm
                                                font-medium text-sky-500 
                                                focus:outline-none cursor-pointer"
                                    >
                                      Checklist
                                    </button>
                                  </div>
                                </div>
                              )}
                              {jobDetails.inspection
                                .final_inspection_done_by && (
                                <div className="flex justify-between mt-2">
                                  <div>
                                    Final done by:{" "}
                                    {
                                      jobDetails.inspection
                                        .final_inspection_done_by
                                    }
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleToggleInspectionChecklistModal(
                                          "F",
                                        )
                                      }
                                      className="inline-flex items-center
                                                px-2 py-1 text-sm
                                                font-medium text-sky-500 
                                                focus:outline-none cursor-pointer"
                                    >
                                      Checklist
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {vendorForm?.id && (
                          <div className="px-4 py-3 flex gap-4 items-start">
                            <dt className="text-md font-bold text-gray-900">
                              Assigned To:
                            </dt>
                            <dd className="text-md text-gray-700 flex-1">
                              {vendorForm?.name}
                            </dd>
                            <div className="ml-auto">
                              <button
                                type="button"
                                onClick={() => setOpenVendorDetails(true)}
                                className="inline-flex items-center rounded border
                                                border-sky-400 bg-white px-2 py-1 text-sm
                                                font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                                focus:outline-none cursor-pointer"
                              >
                                Details
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </dl>
                </div>
              </div>

              {/* SERVICES */}
              <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                <div className="px-4 py-3 bg-gray-100 flex justify-between gap-4">
                  <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                    Services
                  </h3>
                  <div className="flex gap-4">
                    <Switch.Group as="li" className="flex items-center">
                      <div className="flex flex-col">
                        <Switch.Label
                          as="p"
                          className="text-sm text-gray-700"
                          passive
                        >
                          {showActions ? "Hide Scope" : "Show Scope"}
                        </Switch.Label>
                      </div>
                      <Switch
                        checked={showActions}
                        onChange={setShowActions}
                        className={classNames(
                          showActions ? "bg-sky-500" : "bg-gray-200",
                          "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={classNames(
                            showActions ? "translate-x-5" : "translate-x-0",
                            "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                          )}
                        />
                      </Switch>
                    </Switch.Group>
                    {currentUser.isCustomer &&
                      (jobDetails.status === "A" ||
                        jobDetails.status === "U") && (
                        <button
                          type="button"
                          onClick={handleToggleAddServiceModal}
                          className="inline-flex items-center rounded border
                        border-sky-400 bg-white px-2 py-1 text-sm
                        font-medium text-sky-500 shadow-sm hover:bg-gray-50
                        focus:outline-none cursor-pointer"
                        >
                          <span>Add</span>
                        </button>
                      )}
                  </div>
                </div>
                <div
                  className="border-t border-gray-200 overflow-y-auto "
                  style={{ maxHeight: "500px" }}
                >
                  {jobDetails.service_assignments?.length === 0 && (
                    <div className="mx-auto flex justify-center text-center my-8">
                      No services found.
                    </div>
                  )}

                  {jobDetails.service_assignments?.map((service) => (
                    <dl key={service.id} className="divide-y divide-gray-100">
                      <div className="px-4 py-3 flex gap-4 hover:bg-gray-50 border-b border-gray-200">
                        <dt className="text-md flex-1">
                          <div
                            className={`text-gray-900 ${
                              showActions ? "font-medium uppercase py-2" : ""
                            }`}
                          >
                            {service.name}
                            {service.is_exterior_wet_wash &&
                              !jobDetails.airport?.is_wet_wash_allowed && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                                  Not allowed
                                </span>
                              )}
                          </div>
                          {service.project_manager && (
                            <div className="text-gray-500">
                              {service.project_manager}
                            </div>
                          )}
                        </dt>
                        <dd className="text-md text-gray-700 text-right">
                          {currentUser.isCustomer &&
                            (jobDetails.status === "A" ||
                              jobDetails.status === "U") && (
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => removeService(service)}
                                  className="inline-flex items-center rounded border
                                                                            border-sky-400 bg-white px-2 py-1 text-sm
                                                                            font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                                                            focus:outline-none cursor-pointer"
                                >
                                  Remove
                                </button>
                              </div>
                            )}

                          {!currentUser.isCustomer &&
                            service.status === "W" && (
                              <button
                                type="button"
                                onClick={() => completeService(service.id)}
                                className="inline-flex items-center rounded border
                                                                        border-sky-400 bg-white px-2.5 py-1.5 text-md
                                                                        font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                                                        focus:outline-none cursor-pointer"
                              >
                                Complete
                              </button>
                            )}

                          {service.status === "C" && (
                            <div className="flex-shrink-0 flex justify-end">
                              <CheckCircleIcon className="h-10 w-10 text-green-500" />
                            </div>
                          )}
                        </dd>
                      </div>
                      {showActions && (
                        <div className="h-[400px] overflow-y-auto p-5 border border-gray-300 pb-8 bg-blue-50">
                          <p
                            dangerouslySetInnerHTML={{
                              __html: service.description?.replace(
                                /\r\n|\n/g,
                                "<br />",
                              ),
                            }}
                          />
                        </div>
                      )}
                    </dl>
                  ))}
                </div>
              </div>
              {/* RETAINERS */}
              {(currentUser.isAdmin ||
                currentUser.isProjectManager ||
                currentUser.isSuperUser ||
                currentUser.isAccountManager ||
                currentUser.isInternalCoordinator ||
                (currentUser.isCustomer && currentUser.isPremiumMember)) && (
                <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                  <div className="px-4 py-3 bg-gray-100 flex justify-between gap-4">
                    <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                      Retainer Services
                    </h3>
                    {currentUser.isCustomer &&
                      (jobDetails.status === "A" ||
                        jobDetails.status === "U") && (
                        <button
                          type="button"
                          onClick={handleToggleAddRetainerServiceModal}
                          className="inline-flex items-center rounded border
                        border-sky-400 bg-white px-2 py-1 text-sm
                        font-medium text-sky-500 shadow-sm hover:bg-gray-50
                        focus:outline-none cursor-pointer"
                        >
                          <span>Add</span>
                        </button>
                      )}
                  </div>
                  <div
                    className="border-t border-gray-200 overflow-y-auto"
                    style={{ maxHeight: "500px" }}
                  >
                    {jobDetails.retainer_service_assignments?.length === 0 && (
                      <div className="mx-auto flex justify-center text-center my-12">
                        No retainers found.
                      </div>
                    )}

                    {jobDetails.retainer_service_assignments?.map((service) => (
                      <dl key={service.id} className="divide-y divide-gray-100">
                        <div className="px-4 py-3 flex gap-4 hover:bg-gray-50 border-b border-gray-200">
                          <dt className="text-md flex-1">
                            <div
                              className={`text-gray-900 ${
                                showActions ? "font-medium uppercase py-2" : ""
                              }`}
                            >
                              {service.name}
                            </div>
                            {service.project_manager && (
                              <div className="text-gray-500">
                                {service.project_manager}
                              </div>
                            )}
                          </dt>
                          <dd className="text-md text-gray-700 text-right">
                            {currentUser.isCustomer &&
                              (jobDetails.status === "A" ||
                                jobDetails.status === "U") && (
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeRetainerService(service)
                                    }
                                    className="inline-flex items-center rounded border
                                                                                border-sky-400 bg-white px-2 py-1 text-sm
                                                                                font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                                                                focus:outline-none cursor-pointer focus:ring-2
                                                                                focus:ring-sky-500 focus:ring-offset-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}

                            {!currentUser.isCustomer &&
                              service.status === "W" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    completeRetainerService(service.id)
                                  }
                                  className="inline-flex items-center rounded border
                                                                            border-sky-400 bg-white px-2.5 py-1.5 text-md
                                                                            font-medium text-sky-500 shadow-sm hover:bg-gray-50
                                                                            focus:outline-none cursor-pointer focus:ring-2
                                                                            focus:ring-red-500 focus:ring-offset-2"
                                >
                                  Complete
                                </button>
                              )}

                            {service.status === "C" && (
                              <div className="flex-shrink-0 flex justify-end">
                                <CheckCircleIcon className="h-10 w-10 text-green-500" />
                              </div>
                            )}
                          </dd>
                        </div>
                        {showActions && (
                          <div className="h-[400px] overflow-y-auto p-5 border border-gray-300 pb-8 bg-blue-50">
                            <p
                              dangerouslySetInnerHTML={{
                                __html: service.description?.replace(
                                  /\r\n|\n/g,
                                  "<br />",
                                ),
                              }}
                            />
                          </div>
                        )}
                      </dl>
                    ))}
                  </div>
                </div>
              )}
              {/* ATTACHMENTS */}
              <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                <div className="px-4 py-3 bg-gray-100 flex justify-between border-b border-gray-200">
                  <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                    Attachments
                  </h3>
                  {(currentUser.isAdmin ||
                    currentUser.isSuperUser ||
                    currentUser.isAccountManager ||
                    currentUser.isCustomer) && (
                    <button
                      type="button"
                      onClick={handleToggleJobFileUploadModal}
                      className="inline-flex items-center rounded border
                            border-sky-400 bg-white px-2 py-1 text-sm
                            font-medium text-sky-500 shadow-sm hover:bg-gray-50
                            focus:outline-none cursor-pointer"
                    >
                      <span>Add</span>
                    </button>
                  )}
                </div>
                {jobDetails.files?.length === 0 && (
                  <div className="flex justify-center text-center my-12 text-md">
                    No file attachments found.
                  </div>
                )}

                {jobDetails.files?.length > 0 && (
                  <ul className="divide-y divide-gray-200 rounded-md border-b border-gray-200">
                    {jobDetails.files?.map((file) => (
                      <li key={file.id} className="p-6 text-md">
                        <div className="flex flex-wrap justify-between gap-4">
                          <div className="flex gap-1">
                            <PaperClipIcon
                              className="h-5 w-5 flex-shrink-0 text-gray-400 relative top-1"
                              aria-hidden="true"
                            />
                            <div
                              className="text-lg truncate overflow-ellipsis"
                              style={{ maxWidth: "250px" }}
                            >
                              {file.name}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleDeleteJobFile(file)}
                                className="inline-flex w-full justify-center rounded-md border
                                                            border-gray-300 bg-white px-2 py-1 text-base 
                                                            text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2
                                                            focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                              >
                                Delete
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => downloadFile(file)}
                              className="inline-flex w-full justify-center rounded-md border font-medium
                                                        border-gray-300 bg-white px-2 py-1 text-base 
                                                        text-sky-500 shadow-sm focus:outline-none focus:ring-2
                                                        focus:ring-sky-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-between gap-4 mt-1">
                          <div className="text-gray-500 relative top-2 text-md">
                            Uploaded on: {file.created_at}
                          </div>
                          <div>
                            {isAdmin && (
                              <Switch.Group
                                as="li"
                                className="flex items-center justify-between py-2"
                              >
                                <div className="flex flex-col">
                                  <Switch.Label
                                    as="p"
                                    className="text-sm font-medium text-gray-900"
                                    passive
                                  >
                                    Everyone
                                  </Switch.Label>
                                </div>
                                <Switch
                                  checked={file.is_public}
                                  onChange={() => handleToggleFilePublic(file)}
                                  className={classNames(
                                    file.is_public
                                      ? "bg-sky-500"
                                      : "bg-gray-200",
                                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2",
                                  )}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={classNames(
                                      file.is_public
                                        ? "translate-x-5"
                                        : "translate-x-0",
                                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                    )}
                                  />
                                </Switch>
                              </Switch.Group>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* LABOR */}
              {(currentUser.isAdmin ||
                currentUser.isSuperUser ||
                currentUser.isAccountManager ||
                currentUser.isInternalCoordinator) && (
                <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                  <div className="p-4 bg-gray-100">
                    <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                      Labor
                    </h3>
                  </div>
                  <div className="border-t border-gray-200">
                    <dl className="divide-y divide-gray-100">
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Time Spent:
                        </dt>
                        <dd className="text-md text-gray-700">
                          {jobDetails.hours_worked
                            ? jobDetails.hours_worked
                            : 0}{" "}
                          hours{" "}
                          {jobDetails.minutes_worked
                            ? jobDetails.minutes_worked
                            : 0}{" "}
                          minutes
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Number of Workers:
                        </dt>
                        <dd className="text-md text-gray-700">
                          {jobDetails.number_of_workers
                            ? jobDetails.number_of_workers
                            : 0}
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Labor Time:
                        </dt>
                        <dd className="text-md text-gray-700">
                          {jobDetails.labor_time
                            ? jobDetails.labor_time.toFixed(1)
                            : 0}{" "}
                          hr
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}

              {/* INVOICE DETAILS */}
              {(currentUser.isAdmin ||
                currentUser.isSuperUser ||
                currentUser.isAccountManager) && (
                <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                  <div className="p-4 bg-gray-100">
                    <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                      Invoice Details
                    </h3>
                  </div>
                  <div className="border-t border-gray-200">
                    <dl className="divide-y divide-gray-100">
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Internal Additional Cost:
                        </dt>
                        <dd className="text-md text-gray-700">
                          $
                          {invoiceDetails?.internal_additional_cost
                            ? invoiceDetails?.internal_additional_cost
                            : 0}
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Vendor:
                        </dt>
                        <dd className="text-md text-gray-700">
                          {invoiceDetails?.vendor?.name
                            ? invoiceDetails?.vendor?.name
                            : "Not specified"}
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Vendor Charge:
                        </dt>
                        <dd className="text-md text-gray-700">
                          $
                          {invoiceDetails?.vendor?.charge
                            ? invoiceDetails?.vendor?.charge
                            : 0}
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Vendor Additional Cost:
                        </dt>
                        <dd className="text-md text-gray-700">
                          $
                          {invoiceDetails?.vendor?.additional_cost
                            ? invoiceDetails?.vendor?.additional_cost
                            : 0}
                        </dd>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <dt className="text-md font-bold text-gray-900">
                          Subcontractor Profit:
                        </dt>
                        <dd className="text-md text-gray-700">
                          $
                          {invoiceDetails?.subcontractor_profit
                            ? invoiceDetails?.subcontractor_profit
                            : 0}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}

              {jobDetails.follower_emails?.length > 0 &&
                (currentUser.isAdmin ||
                  currentUser.isSuperUser ||
                  currentUser.isAccountManager ||
                  currentUser.isInternalCoordinator) && (
                  <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                    <div className="p-4 bg-gray-100">
                      <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                        Follower Emails
                      </h3>
                    </div>
                    <div className="border-t border-gray-200">
                      <ul className="divide-y divide-gray-200">
                        {jobDetails.follower_emails.map((email) => (
                          <li
                            key={email.id}
                            className="px-4 py-3 flex justify-between gap-4"
                          >
                            <div className="text-md text-gray-700">
                              {email.email}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
            </div>

            {currentUser.canSeePrice &&
            (!currentUser.isExternalProjectManager ||
              (jobDetails.status !== "C" && jobDetails.status !== "I")) ? (
              <div className="grid xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 xs:grid-cols-1 mt-6 gap-6">
                <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                  <div className="p-4 bg-gray-100">
                    <h3 className="text-base font-bold leading-7 text-gray-900 uppercase">
                      Price Breakdown
                    </h3>
                  </div>
                  <div className="border-t border-gray-200">
                    {!jobDetails.is_auto_priced &&
                      !currentUser.isProjectManager && (
                        <div className="p-4 flex gap-4">
                          <dt className="text-md font-bold text-gray-900">
                            Price (manually set):
                          </dt>
                          <dd className="text-md text-gray-700">
                            {"$"}
                            {jobDetails.price
                              ? jobDetails.price.toLocaleString()
                              : "0.00"}
                          </dd>
                        </div>
                      )}

                    {jobDetails.is_auto_priced && (
                      <div>
                        {priceBreakdownComparison &&
                        Object.keys(priceBreakdownComparison).length > 0 ? (
                          <div className="p-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="text-xl text-gray-700">
                                  {
                                    priceBreakdownComparison
                                      .customerPriceBreakdown?.aircraftType
                                  }
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  Compare customer and vendor pricing
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {!currentUser.isProjectManager && (
                                  <>
                                    <span
                                      className="relative bg-sky-100 text-sky-500 rounded-md p-1 font-medium text-sm"
                                      style={{ top: "2px" }}
                                    >
                                      {
                                        priceBreakdownComparison
                                          .customerPriceBreakdown?.priceListType
                                      }
                                    </span>
                                    <span
                                      className="relative bg-violet-100 text-violet-600 rounded-md p-1 font-medium text-sm"
                                      style={{ top: "2px" }}
                                    >
                                      {
                                        priceBreakdownComparison
                                          .vendorPriceBreakdown?.priceListType
                                      }
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            {priceBreakdownComparison?.assigned_project_manager_info && (
                              <div className="my-6 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-3">
                                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-100 text-sky-600 font-semibold text-sm shadow-sm">
                                        {
                                          priceBreakdownComparison
                                            .assigned_project_manager_info
                                            .first_name?.[0]
                                        }
                                        {
                                          priceBreakdownComparison
                                            .assigned_project_manager_info
                                            .last_name?.[0]
                                        }
                                      </div>

                                      <div className="min-w-0">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Assigned Project Manager
                                        </div>
                                        <div className="text-sm font-semibold text-slate-900 truncate">
                                          {
                                            priceBreakdownComparison
                                              .assigned_project_manager_info
                                              .first_name
                                          }{" "}
                                          {
                                            priceBreakdownComparison
                                              .assigned_project_manager_info
                                              .last_name
                                          }
                                        </div>
                                      </div>
                                    </div>

                                    <p className="mt-3 text-sm text-slate-600">
                                      Control whether this assigned project
                                      manager can view pricing details for this
                                      workflow.
                                    </p>
                                  </div>

                                  <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm lg:min-w-[260px]">
                                    <div>
                                      <div className="text-sm font-medium text-slate-900">
                                        Can view prices
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        Enable or disable price visibility
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                                          priceBreakdownComparison
                                            .assigned_project_manager_info
                                            .show_price_breakdown
                                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                            : "bg-slate-100 text-slate-600 ring-slate-200"
                                        }`}
                                      >
                                        {priceBreakdownComparison
                                          .assigned_project_manager_info
                                          .show_price_breakdown
                                          ? "Enabled"
                                          : "Disabled"}
                                      </span>

                                      <Switch
                                        checked={
                                          !!priceBreakdownComparison
                                            .assigned_project_manager_info
                                            .show_price_breakdown
                                        }
                                        onChange={
                                          handleToggleAssignedPmPriceVisibility
                                        }
                                        disabled={
                                          savingAssignedPmPriceVisibility
                                        }
                                        className={classNames(
                                          priceBreakdownComparison
                                            .assigned_project_manager_info
                                            .show_price_breakdown
                                            ? "bg-sky-600"
                                            : "bg-gray-200",
                                          savingAssignedPmPriceVisibility
                                            ? "opacity-60 cursor-not-allowed"
                                            : "cursor-pointer",
                                          "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2",
                                        )}
                                      >
                                        <span className="sr-only">
                                          Toggle project manager price
                                          visibility
                                        </span>
                                        <span
                                          aria-hidden="true"
                                          className={classNames(
                                            priceBreakdownComparison
                                              .assigned_project_manager_info
                                              .show_price_breakdown
                                              ? "translate-x-5"
                                              : "translate-x-0",
                                            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                          )}
                                        />
                                      </Switch>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="mt-6 overflow-x-auto">
                              <div className="min-w-[700px]">
                                <div className="grid grid-cols-12 gap-4 border-b border-gray-300 pb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                                  <div className="col-span-6">Item</div>
                                  <div className="col-span-3 text-right">
                                    Customer
                                  </div>
                                  <div className="col-span-3 text-right">
                                    Vendor
                                  </div>
                                </div>

                                {/* SERVICES */}
                                <div className="mt-4">
                                  <h3 className="text-lg text-gray-700 font-medium">
                                    Services
                                  </h3>
                                  <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                    {(
                                      priceBreakdownComparison
                                        .customerPriceBreakdown?.services || []
                                    ).map((customerService, index) => {
                                      const vendorService =
                                        priceBreakdownComparison
                                          .vendorPriceBreakdown?.services?.[
                                          index
                                        ];

                                      return (
                                        <div
                                          key={
                                            customerService.id ??
                                            `${customerService.name}-${index}`
                                          }
                                          className="grid grid-cols-12 gap-4 py-2 text-md hover:bg-gray-50"
                                        >
                                          <dt className="col-span-6 text-gray-500 pr-2 truncate">
                                            {customerService.name}
                                          </dt>
                                          <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                            {Number(customerService.price) > 0
                                              ? "$" +
                                                Number(
                                                  customerService.price,
                                                ).toLocaleString()
                                              : "TBD"}
                                          </dd>
                                          <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                            {Number(vendorService?.price) > 0
                                              ? "$" +
                                                Number(
                                                  vendorService?.price,
                                                ).toLocaleString()
                                              : "TBD"}
                                          </dd>
                                        </div>
                                      );
                                    })}
                                  </dl>

                                  <div className="grid grid-cols-12 gap-4 pt-2 text-lg mt-1">
                                    <dt className="col-span-6 text-gray-500 font-medium">
                                      Subtotal
                                    </dt>
                                    <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                      $
                                      {
                                        priceBreakdownComparison
                                          .customerPriceBreakdown?.servicesPrice
                                      }
                                    </dd>
                                    <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                      $
                                      {
                                        priceBreakdownComparison
                                          .vendorPriceBreakdown?.servicesPrice
                                      }
                                    </dd>
                                  </div>
                                </div>

                                {/* DISCOUNTS */}
                                {((priceBreakdownComparison
                                  .customerPriceBreakdown?.discounts?.length ??
                                  0) > 0 ||
                                  (priceBreakdownComparison.vendorPriceBreakdown
                                    ?.discounts?.length ?? 0) > 0) && (
                                  <div className="mt-6">
                                    <h3 className="text-lg text-gray-700 font-medium">
                                      Discounts Applied
                                    </h3>

                                    <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                      {(
                                        priceBreakdownComparison
                                          .customerPriceBreakdown?.discounts ||
                                        []
                                      ).map((customerDiscount, index) => {
                                        const vendorDiscount =
                                          priceBreakdownComparison
                                            .vendorPriceBreakdown?.discounts?.[
                                            index
                                          ];

                                        const discountLabel =
                                          customerDiscount.name === "S"
                                            ? "By Service"
                                            : customerDiscount.name === "A"
                                              ? "By Airport"
                                              : customerDiscount.name === "G"
                                                ? "General"
                                                : customerDiscount.name;

                                        return (
                                          <div
                                            key={
                                              customerDiscount.id ??
                                              `${customerDiscount.name}-${index}`
                                            }
                                            className="grid grid-cols-12 gap-4 py-2 text-md hover:bg-gray-50"
                                          >
                                            <dt className="col-span-6 text-gray-500 pr-2 truncate">
                                              {discountLabel}
                                            </dt>

                                            <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                              {customerDiscount.isPercentage ? (
                                                <>
                                                  {customerDiscount.discount}%
                                                  <span className="text-gray-500">
                                                    {" "}
                                                    ($
                                                    {
                                                      customerDiscount.discount_dollar_amount
                                                    }
                                                    )
                                                  </span>
                                                </>
                                              ) : (
                                                <>
                                                  ${customerDiscount.discount}
                                                </>
                                              )}
                                            </dd>

                                            <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                              {vendorDiscount ? (
                                                vendorDiscount.isPercentage ? (
                                                  <>
                                                    {vendorDiscount.discount}%
                                                    <span className="text-gray-500">
                                                      {" "}
                                                      ($
                                                      {
                                                        vendorDiscount.discount_dollar_amount
                                                      }
                                                      )
                                                    </span>
                                                  </>
                                                ) : (
                                                  <>
                                                    ${vendorDiscount.discount}
                                                  </>
                                                )
                                              ) : (
                                                "-"
                                              )}
                                            </dd>
                                          </div>
                                        );
                                      })}
                                    </dl>

                                    <div className="grid grid-cols-12 gap-4 py-2 text-lg mt-1">
                                      <dt className="col-span-6 text-gray-500 font-medium">
                                        Subtotal
                                      </dt>
                                      <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                        $
                                        {
                                          priceBreakdownComparison
                                            .customerPriceBreakdown
                                            ?.discountedPrice
                                        }
                                      </dd>
                                      <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                        $
                                        {
                                          priceBreakdownComparison
                                            .vendorPriceBreakdown
                                            ?.discountedPrice
                                        }
                                      </dd>
                                    </div>
                                  </div>
                                )}

                                {/* ADDITIONAL FEES */}
                                {((priceBreakdownComparison
                                  .customerPriceBreakdown?.additionalFees
                                  ?.length ?? 0) > 0 ||
                                  (priceBreakdownComparison.vendorPriceBreakdown
                                    ?.additionalFees?.length ?? 0) > 0) && (
                                  <div className="mt-6">
                                    <h3 className="text-lg text-gray-700 font-medium">
                                      Additional Fees Applied
                                    </h3>

                                    <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                      {(
                                        priceBreakdownComparison
                                          .customerPriceBreakdown
                                          ?.additionalFees || []
                                      ).map((customerFee, index) => {
                                        const vendorFee =
                                          priceBreakdownComparison
                                            .vendorPriceBreakdown
                                            ?.additionalFees?.[index];

                                        const feeLabel =
                                          customerFee.name === "A"
                                            ? "Travel Fees"
                                            : customerFee.name === "F"
                                              ? "FBO Fee"
                                              : customerFee.name === "G"
                                                ? "General"
                                                : customerFee.name === "V"
                                                  ? "Vendor Price Difference"
                                                  : customerFee.name === "M"
                                                    ? "Management Fees"
                                                    : customerFee.name;

                                        return (
                                          <div
                                            key={
                                              customerFee.id ??
                                              `${customerFee.name}-${index}`
                                            }
                                            className="grid grid-cols-12 gap-4 py-2 text-md hover:bg-gray-50"
                                          >
                                            <dt className="col-span-6 text-gray-500 pr-2 truncate">
                                              {feeLabel}
                                            </dt>

                                            <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                              {customerFee.isPercentage ? (
                                                <>
                                                  {customerFee.fee}%
                                                  <span className="text-gray-500">
                                                    {" "}
                                                    ($
                                                    {
                                                      customerFee.additional_fee_dollar_amount
                                                    }
                                                    )
                                                  </span>
                                                </>
                                              ) : customerFee.name === "M" ? (
                                                <>
                                                  $
                                                  {
                                                    customerFee.additional_fee_dollar_amount
                                                  }
                                                </>
                                              ) : (
                                                <>${customerFee.fee}</>
                                              )}
                                            </dd>

                                            <dd className="col-span-3 whitespace-nowrap text-right text-gray-900">
                                              {vendorFee ? (
                                                vendorFee.isPercentage ? (
                                                  <>
                                                    {vendorFee.fee}%
                                                    <span className="text-gray-500">
                                                      {" "}
                                                      ($
                                                      {
                                                        vendorFee.additional_fee_dollar_amount
                                                      }
                                                      )
                                                    </span>
                                                  </>
                                                ) : vendorFee.name === "M" ? (
                                                  <>
                                                    $
                                                    {
                                                      vendorFee.additional_fee_dollar_amount
                                                    }
                                                  </>
                                                ) : (
                                                  <>${vendorFee.fee}</>
                                                )
                                              ) : (
                                                "-"
                                              )}
                                            </dd>
                                          </div>
                                        );
                                      })}
                                    </dl>
                                  </div>
                                )}

                                {/* TOTAL */}
                                <div className="grid grid-cols-12 gap-4 pb-4 pt-3 text-lg">
                                  <dt className="col-span-6 text-gray-500 font-medium">
                                    Total
                                  </dt>
                                  <dd className="col-span-3 whitespace-nowrap text-right text-gray-900 font-semibold">
                                    $
                                    {
                                      priceBreakdownComparison
                                        .customerPriceBreakdown?.totalPrice
                                    }
                                  </dd>
                                  <dd className="col-span-3 whitespace-nowrap text-right text-gray-900 font-semibold">
                                    $
                                    {
                                      priceBreakdownComparison
                                        .vendorPriceBreakdown?.totalPrice
                                    }
                                  </dd>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4">
                            <div className="flex justify-between text-md">
                              <div className="text-xl text-gray-700">
                                {priceBreakdown.aircraftType}
                              </div>
                              <div>
                                {!currentUser.isProjectManager && (
                                  <span
                                    className="relative bg-sky-100 text-sky-500 rounded-md p-1 font-medium"
                                    style={{ top: "2px" }}
                                  >
                                    {priceBreakdown.priceListType}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="mt-4">
                              <h3 className="text-lg text-gray-700 font-medium">
                                Services
                              </h3>
                              <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                {priceBreakdown.services?.map((service) => (
                                  <div
                                    key={service.id}
                                    className="flex justify-between py-2 text-lg hover:bg-gray-50"
                                  >
                                    <dt className="text-gray-500 pr-2 truncate">
                                      {service.name}
                                    </dt>
                                    <dd className="whitespace-nowrap text-gray-900">
                                      {service.price > 0
                                        ? "$" + service.price
                                        : "TBD"}
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                              <div className="flex justify-end pt-2 text-lg mt-1">
                                <dt className="text-gray-500 pr-2 text-right font-medium">
                                  Subtotal
                                </dt>
                                <dd className="whitespace-nowrap text-gray-900">
                                  ${priceBreakdown.servicesPrice}
                                </dd>
                              </div>
                            </div>

                            {(currentUser.isAdmin ||
                              currentUser.isSuperUser ||
                              currentUser.isAccountManager ||
                              currentUser.isInternalCoordinator ||
                              currentUser.isCustomer) && (
                              <>
                                {priceBreakdown.discounts?.length > 0 && (
                                  <div className="">
                                    <h3 className="text-lg text-gray-700 font-medium">
                                      Discounts Applied
                                    </h3>
                                    <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                      {priceBreakdown.discounts.map(
                                        (discount) => (
                                          <div
                                            key={discount.id}
                                            className="flex justify-between py-2 text-lg hover:bg-gray-50"
                                          >
                                            <dt className="text-gray-500 pr-2 truncate">
                                              {discount.name === "S"
                                                ? "By Service"
                                                : ""}
                                              {discount.name === "A"
                                                ? "By Airport"
                                                : ""}
                                              {discount.name === "G"
                                                ? "General"
                                                : ""}
                                            </dt>
                                            <dd className="whitespace-nowrap text-gray-900">
                                              {discount.isPercentage && (
                                                <>
                                                  {discount.discount}
                                                  {"%"}
                                                  <span className="text-gray-500">
                                                    {" ("}$
                                                    {
                                                      discount.discount_dollar_amount
                                                    }
                                                    {")"}
                                                  </span>
                                                </>
                                              )}

                                              {!discount.isPercentage && (
                                                <>
                                                  {"$"}
                                                  {discount.discount}
                                                </>
                                              )}
                                            </dd>
                                          </div>
                                        ),
                                      )}
                                    </dl>
                                    <div className="flex justify-end py-2 text-lg mt-1">
                                      <dt className="text-gray-500 pr-2 text-right font-medium">
                                        Subtotal
                                      </dt>
                                      <dd className="whitespace-nowrap text-gray-900">
                                        ${priceBreakdown.discountedPrice}
                                      </dd>
                                    </div>
                                  </div>
                                )}

                                {priceBreakdown.additionalFees?.length > 0 && (
                                  <div className="">
                                    <h3 className="text-lg text-gray-700">
                                      Additional Fees Applied
                                    </h3>
                                    <dl className="mt-2 divide-y divide-gray-200 border-b border-gray-200">
                                      {priceBreakdown.additionalFees.map(
                                        (fee) => (
                                          <div
                                            key={fee.id}
                                            className="flex justify-between py-2 text-lg hover:bg-gray-50"
                                          >
                                            <dt className="text-gray-500 pr-2 truncate">
                                              {fee.name === "A"
                                                ? "Travel Fees"
                                                : ""}
                                              {fee.name === "F"
                                                ? "FBO Fee"
                                                : ""}
                                              {fee.name === "G"
                                                ? "General"
                                                : ""}
                                              {fee.name === "V"
                                                ? "Vendor Price Difference"
                                                : ""}
                                              {fee.name === "M"
                                                ? "Management Fees"
                                                : ""}
                                            </dt>
                                            <dd className="whitespace-nowrap text-gray-900">
                                              {fee.isPercentage && (
                                                <>
                                                  {fee.fee}
                                                  {"%"}
                                                  <span className="text-gray-500">
                                                    {" ("}$
                                                    {
                                                      fee.additional_fee_dollar_amount
                                                    }
                                                    {")"}
                                                  </span>
                                                </>
                                              )}

                                              {!fee.isPercentage &&
                                                fee.name !== "M" && (
                                                  <>
                                                    {"$"}
                                                    {fee.fee}
                                                  </>
                                                )}

                                              {!fee.isPercentage &&
                                                fee.name === "M" && (
                                                  <>
                                                    {"$"}
                                                    {
                                                      fee.additional_fee_dollar_amount
                                                    }
                                                  </>
                                                )}
                                            </dd>
                                          </div>
                                        ),
                                      )}
                                    </dl>
                                  </div>
                                )}

                                <div className="flex justify-end pb-4 text-lg">
                                  <dt className="text-gray-500 pr-2 text-right font-medium">
                                    Total
                                  </dt>
                                  <dd className="whitespace-nowrap text-gray-900">
                                    ${priceBreakdown.totalPrice}
                                  </dd>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <JobComments />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 mt-6 gap-6">
                <JobComments />
              </div>
            )}

            {flights.length > 0 && (
              <div className="grid xl:grid-cols-1 lg:grid-cols-1 md:grid-cols-1 sm:grid-cols-1 xs:grid-cols-1 mt-6 gap-6">
                <div className="relative overflow-hidden rounded-lg border border-gray-300 ">
                  <div className="p-4 bg-gray-100">
                    <h3 className="text-base font-bold leading-7 text-gray-900 uppercase">
                      Flights
                    </h3>
                  </div>
                  <div className="border-t border-gray-200">
                    <ul role="list" className="divide-y divide-gray-200">
                      {flights.map((flight, index) => (
                        <li
                          key={index}
                          className="relative py-5 hover:bg-gray-50"
                        >
                          <div className="px-4 sm:px-6 lg:px-8">
                            <div className="mx-auto flex max-w-4xl justify-end text-right">
                              <div className="flex justify-end text-right">
                                <div
                                  class={`rounded-md cursor-pointer border
                                ${
                                  flight.status === "Scheduled" &&
                                  "border-blue-600 text-blue-600"
                                }
                                ${
                                  flight.status === "Arrived" &&
                                  "border-green-600 text-green-600"
                                }
                                ${
                                  flight.status === "En Route" &&
                                  "border-yellow-600 text-yellow-600"
                                }
                                         py-2 px-2 flex items-center justify-center text-sm
                                         uppercase`}
                                >
                                  {flight.status}
                                </div>
                              </div>
                            </div>
                            <div className="mx-auto flex max-w-4xl justify-between gap-x-6 mt-2">
                              <div className="">
                                <div className="font-medium text-xl">
                                  {flight.origin.code}
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="overflow-hidden rounded-full bg-gray-200 mt-3">
                                  <div
                                    style={{
                                      width: flight.progress_percent + "%",
                                    }}
                                    className="h-2 rounded-full bg-green-500"
                                  />
                                </div>
                              </div>
                              <div className="">
                                <div className="font-medium text-xl">
                                  {flight.destination?.code}
                                </div>
                              </div>
                            </div>
                            <div
                              className="mx-auto max-w-4xl
                                        grid xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 xs:grid-cols-1
                                         gap-6 mt-4 divide-x divide-gray-200 mb-4"
                            >
                              <div className="px-2">
                                <div className="font-medium text-lg">
                                  DEPARTURE
                                </div>
                                <div>{flight.origin.city}</div>
                                <div className="text-gray-400">
                                  {flight.origin.timezone}
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div>
                                    <div>Scheduled</div>
                                    <div>{flight.local_scheduled_off}</div>
                                  </div>
                                  <div>
                                    <div>Estimated Takeoff</div>
                                    <div>
                                      {flight.local_estimated_off
                                        ? flight.local_estimated_off
                                        : "Not specified"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="px-2">
                                <div className="flex justify-between gap-2">
                                  <div className="font-medium text-log">
                                    ARRIVAL
                                  </div>
                                  {flight.arrival_delay > 0 && (
                                    <div className="text-right">
                                      <p className="inline-flex text-sm text-white rounded-md py-1 px-2 bg-red-400">
                                        Delayed
                                      </p>
                                    </div>
                                  )}
                                </div>
                                <div>{flight.destination?.city}</div>
                                <div className="text-gray-400">
                                  {flight.destination?.timezone}
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <div>
                                    <div>Scheduled</div>
                                    <div>{flight.local_scheduled_on}</div>
                                  </div>
                                  <div>
                                    <div>Estimated Landing</div>
                                    <div>
                                      {flight.local_estimated_on
                                        ? flight.local_estimated_on
                                        : "Not specified"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {serviceActivities.length > 0 && (
              <div className="mt-6">
                <div className="relative overflow-hidden rounded-lg border border-gray-300">
                  <div className="p-4 bg-gray-100">
                    <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">
                      Tail History
                    </h3>
                  </div>
                  <div className="border-t border-gray-200">
                    <div className="p-4">
                      {serviceActivities.length > 0 && (
                        <table className="min-w-full table-auto">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 uppercase tracking-wide"
                              >
                                <div className="flex gap-1">Date</div>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                              >
                                P.O
                              </th>
                              {!currentUser.isCustomer && (
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                                >
                                  Customer
                                </th>
                              )}
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                              >
                                Tail
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                              >
                                Airport
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                              >
                                FBO
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide"
                              >
                                Service
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {serviceActivities.map((service) => (
                              <tr key={service.id}>
                                <td className="px-2 py-2 text-md text-gray-500 sm:pl-0">
                                  {service.timestamp}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-md text-sky-500 font-semibold cursor-pointer">
                                  <Link
                                    to={`/create-job/review/${service.job_id}`}
                                  >
                                    {service.purchase_order}
                                  </Link>
                                </td>
                                {!currentUser.isCustomer && (
                                  <td className="whitespace-nowrap px-3 py-2 text-md text-gray-500">
                                    <div className="truncate overflow-ellipsis w-60">
                                      {service.customer_name}
                                    </div>
                                  </td>
                                )}
                                <td className="whitespace-nowrap px-3 py-2 text-md text-gray-500">
                                  {service.tail_number}
                                </td>
                                <td className="px-3 py-2 text-md text-gray-500">
                                  <div className="truncate overflow-ellipsis w-40">
                                    {service.airport_name}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-md text-gray-500">
                                  {service.fbo_name}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-md text-gray-500">
                                  <div className=" truncate overflow-ellipsis w-96">
                                    {service.service_name}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

      <Transition.Root show={openFboDetails} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setOpenFboDetails}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              {/* Full width on small screens, 50% on md+ */}
              <Dialog.Panel className="relative ml-auto flex h-full w-full md:w-1/2 max-w-none flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    FBO Details
                  </h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => setOpenFboDetails(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="px-4 mt-3">
                  <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {jobDetails.fbo?.name || "FBO"}
                      </h3>
                      {!canEditFbo && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                          View only
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 px-4">
                  <div className="space-y-6">
                    {/* Hours of Operation */}
                    <div>
                      <label
                        htmlFor="hours_of_operation"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Hours of Operation
                      </label>
                      <textarea
                        id="hours_of_operation"
                        name="hours_of_operation"
                        rows={3}
                        value={fboForm.hours_of_operation}
                        onChange={canEditFbo ? handleFboChange : undefined}
                        readOnly={!canEditFbo}
                        aria-readonly={!canEditFbo}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 ${
                          !canEditFbo
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="e.g., Mon–Fri 06:00–22:00 • Sat–Sun 08:00–20:00"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use any readable format.
                      </p>
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label
                        htmlFor="phone_number"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Phone Number
                      </label>
                      <input
                        id="phone_number"
                        name="phone_number"
                        type="tel"
                        value={fboForm.phone_number}
                        onChange={canEditFbo ? handleFboChange : undefined}
                        readOnly={!canEditFbo}
                        aria-readonly={!canEditFbo}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-sky-500 focus:ring-sky-500 border-gray-300 ${
                          !canEditFbo
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="+1 (555) 123-4567 or comma separated"
                      />
                    </div>

                    {/* Website */}
                    <div>
                      <label
                        htmlFor="website"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Website
                      </label>
                      <input
                        id="website"
                        name="website"
                        type="url"
                        value={fboForm.website}
                        onChange={canEditFbo ? handleFboChange : undefined}
                        readOnly={!canEditFbo}
                        aria-readonly={!canEditFbo}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-sky-500 focus:ring-sky-500 border-gray-300 ${
                          !canEditFbo
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="https://example.com"
                      />
                    </div>

                    {/* Special Notes */}
                    <div>
                      <label
                        htmlFor="special_notes"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Special Notes
                      </label>
                      <textarea
                        id="special_notes"
                        name="special_notes"
                        rows={4}
                        value={fboForm.special_notes}
                        onChange={canEditFbo ? handleFboChange : undefined}
                        readOnly={!canEditFbo}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 ${
                          !canEditFbo
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="e.g., Ramp access instructions, security requirements, parking notes…"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  {canEditFbo && (
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                        onClick={() => setOpenFboDetails(false)}
                        disabled={savingFbo}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveFbo()}
                        disabled={savingFbo}
                        className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                          savingFbo
                            ? "bg-sky-300"
                            : "bg-sky-600 hover:bg-sky-700"
                        }`}
                      >
                        {savingFbo ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={openAirportDetails} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          onClose={setOpenAirportDetails}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              {/* Full width on small screens, 50% on md+ */}
              <Dialog.Panel className="relative ml-auto flex h-full w-full md:w-1/2 max-w-none flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Airport Details
                  </h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => setOpenAirportDetails(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="px-4 mt-3">
                  <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {jobDetails.airport?.name || "Airport"}
                      </h3>
                      {!canEditAirport && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                          View only
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* is wet wash allowed. Add a toggle here controlled by airportForm.is_wet_wash_allowed */}
                <div className="mt-4 px-4">
                  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <Switch.Group
                      as="div"
                      className="flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <Switch.Label className="text-sm font-semibold text-gray-900">
                          Is Wet Wash allowed at this airport?
                        </Switch.Label>
                        <Switch.Description className="mt-1 text-sm text-gray-600">
                          Toggle whether wet wash is permitted at this airport.
                          This setting is used for validations and warnings
                          during job creation.
                        </Switch.Description>

                        {!canEditAirport && (
                          <div className="mt-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                            View only
                          </div>
                        )}
                      </div>

                      <Switch
                        checked={!!airportForm.is_wet_wash_allowed}
                        onChange={handleToggleWetWashAllowed}
                        disabled={!canEditAirport}
                        className={classNames(
                          airportForm.is_wet_wash_allowed
                            ? "bg-sky-600"
                            : "bg-gray-200",
                          !canEditAirport
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer",
                          "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2",
                        )}
                      >
                        <span className="sr-only">Toggle wet wash allowed</span>
                        <span
                          aria-hidden="true"
                          className={classNames(
                            airportForm.is_wet_wash_allowed
                              ? "translate-x-5"
                              : "translate-x-0",
                            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                          )}
                        />
                      </Switch>
                    </Switch.Group>

                    {/* Optional: quick status pill */}
                    <div className="mt-3">
                      {airportForm.is_wet_wash_allowed ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          Allowed
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                          Not allowed
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 px-4">
                  <div className="space-y-6">
                    {/* Special Notes */}
                    <div>
                      <label
                        htmlFor="special_notes"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Special Notes
                      </label>
                      <textarea
                        id="special_notes"
                        name="special_notes"
                        rows={4}
                        value={airportForm.special_notes}
                        onChange={
                          canEditAirport ? handleAirportChange : undefined
                        }
                        readOnly={!canEditAirport}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 ${
                          !canEditAirport
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="e.g. Wet Wash is allowed at this airport only if the aircraft is inside a hangar.…"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  {canEditAirport && (
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                        onClick={() => setOpenAirportDetails(false)}
                        disabled={savingAirport}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveAirport()}
                        disabled={savingAirport}
                        className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                          savingAirport
                            ? "bg-sky-300"
                            : "bg-sky-600 hover:bg-sky-700"
                        }`}
                      >
                        {savingAirport ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={openVendorDetails} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          onClose={setOpenVendorDetails}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              {/* Full width on small screens, 50% on md+ */}
              <Dialog.Panel className="relative ml-auto flex h-full w-full md:w-1/2 max-w-none flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Vendor Details
                  </h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                    onClick={() => setOpenVendorDetails(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="px-4 mt-3">
                  <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {vendorForm.name || "Vendor"}
                      </h3>
                      {!canEditVendor && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
                          View only
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 px-4">
                  <div className="space-y-6">
                    {/* Hours of Operation */}
                    <div>
                      <label
                        htmlFor="hours_of_operation"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Billing Address
                      </label>
                      <textarea
                        id="hours_of_operation"
                        name="billing_address"
                        rows={3}
                        value={vendorForm.billing_address}
                        onChange={
                          canEditVendor ? handleVendorChange : undefined
                        }
                        readOnly={!canEditVendor}
                        aria-readonly={!canEditVendor}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 ${
                          !canEditVendor
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="1234 Main St. Apt, City, State, ZIP"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use any readable format.
                      </p>
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label
                        htmlFor="phone_numbers"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Phone Numbers
                      </label>
                      <input
                        id="phone_numbers"
                        name="phone_numbers"
                        type="tel"
                        value={vendorForm.phone_numbers}
                        onChange={
                          canEditVendor ? handleVendorChange : undefined
                        }
                        readOnly={!canEditVendor}
                        aria-readonly={!canEditVendor}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-sky-500 focus:ring-sky-500 border-gray-300 ${
                          !canEditVendor
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="+1 (555) 123-4567 or comma separated"
                      />
                    </div>

                    {/* Website */}
                    <div>
                      <label
                        htmlFor="emails"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Emails
                      </label>
                      <input
                        id="emails"
                        name="emails"
                        type="url"
                        value={vendorForm.emails}
                        onChange={
                          canEditVendor ? handleVendorChange : undefined
                        }
                        readOnly={!canEditVendor}
                        aria-readonly={!canEditVendor}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-sky-500 focus:ring-sky-500 border-gray-300 ${
                          !canEditVendor
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="https://example.com or comma separated"
                      />
                    </div>

                    {/* Special Notes */}
                    <div>
                      <label
                        htmlFor="notes"
                        className="block text-md font-extrabold text-gray-900"
                      >
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        rows={4}
                        value={vendorForm.notes}
                        onChange={
                          canEditVendor ? handleVendorChange : undefined
                        }
                        readOnly={!canEditVendor}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 ${
                          !canEditVendor
                            ? "bg-gray-50 text-gray-600 cursor-not-allowed"
                            : ""
                        }`}
                        placeholder="e.g., Ramp access instructions, security requirements, parking notes…"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  {canEditVendor && (
                    <div className="mt-6 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
                        onClick={() => setOpenVendorDetails(false)}
                        disabled={savingVendor}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveVendor()}
                        disabled={savingVendor}
                        className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                          savingVendor
                            ? "bg-sky-300"
                            : "bg-sky-600 hover:bg-sky-700"
                        }`}
                      >
                        {savingVendor ? "Saving…" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {isCancelJobModalOpen && (
        <JobCancelModal
          isOpen={isCancelJobModalOpen}
          jobDetails={jobDetails}
          handleClose={handleToggleJobCancelModal}
          updateJobStatus={updateJobStatus}
        />
      )}
      {isReturnJobModalOpen && (
        <JobReturnModal
          isOpen={isReturnJobModalOpen}
          jobDetails={jobDetails}
          handleClose={handleToggleJobReturnModal}
          returnJob={returnJob}
        />
      )}
      {isCompleteJobModalOpen && (
        <JobCompleteModal
          isOpen={isCompleteJobModalOpen}
          jobDetails={jobDetails}
          handleClose={handleToggleJobCompleteModal}
          completeJob={completeJob}
          currentUser={currentUser}
        />
      )}
      {isPriceBreakdownModalOpen && (
        <JobPriceBreakdownModal
          isOpen={isPriceBreakdownModalOpen}
          jobDetails={jobDetails}
          handleClose={handleTogglePriceBreakdownModal}
          hidePricingAdjustments={Boolean(
            currentUser.isProjectManager ||
              currentUser.isExternalProjectManager ||
              currentUser.isMasterPM,
          )}
        />
      )}
      {isAddServiceModalOpen && (
        <AddServiceModal
          isOpen={isAddServiceModalOpen}
          handleClose={handleToggleAddServiceModal}
          existingServices={[]}
          projectManagers={[]}
          handleAddService={handleAddService}
          jobId={jobId}
        />
      )}

      {isCategoriesModalOpen && (
        <JobCategoriesModal
          isOpen={isCategoriesModalOpen}
          handleClose={handleToggleCategoriesModal}
          handleUpdateCategories={handleUpdateCategories}
          customerId={jobDetails.customer?.id}
          jobId={jobId}
          existingJobCategories={jobDetails.categories}
        />
      )}

      {isJobFileUploadModalOpen && (
        <JobFileUploadModal
          isOpen={isJobFileUploadModalOpen}
          handleClose={handleToggleJobFileUploadModal}
          isAdmin={isAdmin}
          jobDetails={jobDetails}
          addJobFile={addJobFile}
        />
      )}

      {isInitialInspectionModalOpen && (
        <JobInitialInspectionModal
          isOpen={isInitialInspectionModalOpen}
          handleClose={handleToggleInitialInspectionModal}
          startJob={startJob}
          jobDetails={jobDetails}
        />
      )}

      {isInspectionChecklistModalOpen && (
        <JobInspectionChecklistModal
          isOpen={isInspectionChecklistModalOpen}
          handleClose={handleToggleInspectionChecklistModal}
          inspectionType={inspectionType}
        />
      )}

      {isAddRetainerServiceModalOpen && (
        <AddRetainerServiceModal
          isOpen={isAddRetainerServiceModalOpen}
          handleClose={handleToggleAddRetainerServiceModal}
          existingServices={[]}
          projectManagers={[]}
          handleAddService={handleAddRetainerService}
          jobId={jobId}
        />
      )}
      {isJobInvoiceModalOpen && (
        <JobInvoiceModal
          isOpen={isJobInvoiceModalOpen}
          handleClose={handleToggleJobInvoiceModal}
          invoiceDetails={invoiceDetails}
        />
      )}

      {isJobNotInvoiceModalOpen && (
        <JobNotInvoiceModal
          isOpen={isJobNotInvoiceModalOpen}
          handleClose={handleToggleJobNotInvoiceModal}
          invoiceDetails={invoiceDetails}
        />
      )}
    </div>
  );
};

export default JobInfo;
